import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { EndeavorApi, EventApi, EventTypeApi, AssignmentApi, AuthApi, AudioContentApi, FlashcardApi, QuizApi } from '../services/ApiService';
import { AiHealthApi } from '../services/ai/AiHealthApi'; // ✅ ADD: Import AiHealthApi
import { DocumentApi } from '../services/api/DocumentApi'; // ✅ ADD: Import DocumentApi for caching
import { DocumentTypeApi } from '../services/api/DocumentTypeApi'; // ✅ NEW: Import for document types
import { DocumentNotesApi } from '../services/api/DocumentNotesApi'; // ✅ NEW: Import for notes caching
import { EnrollmentApi } from '../services/api/EnrollmentApi';
import { ProgressApi } from '../services/api/ProgressApi';
import { useAuth } from './AuthContext';
import UsageEventsTracker from '../services/UsageEventsTracker';
import { buildAssignmentUpdatePayload } from '../utils/assignmentPayload';
import {
  normEndeavorId,
  normEventId,
  normAssignmentId,
  normDocumentId,
  normDueDate,
  normStartDate,
  normalizeAssignment,
  normalizeEvent,
} from './dataUtils';
import * as selectors from './dataSelectors';

// ✅ NEW: Log level control to reduce console noise
const CACHE_LOG_LEVEL = process.env.REACT_APP_CACHE_LOG_LEVEL || 'warn';

// Cache timeout for /full endpoint: 12 hours
const FULL_CACHE_TIMEOUT = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

// Helper functions for conditional logging
const logDebug = (...args) => {
  if (CACHE_LOG_LEVEL === 'debug') console.log(...args);
};

const logInfo = (...args) => {
  if (CACHE_LOG_LEVEL === 'debug' || CACHE_LOG_LEVEL === 'info') console.log(...args);
};

const DataContext = createContext();

/**
 * Non-throwing variant of useData — returns null when rendered outside DataProvider.
 * Use this in hooks/components that may mount before or outside the provider tree.
 */
export const useDataOptional = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const { setDataContext, user } = useAuth();
  
  // Canonical state: plain objects for easier debugging and immutability
  const [state, setState] = useState({
    assignmentsById: {},
    eventsById: {},
    documentsById: {},
    coursesById: {}, // endeavors
    documentStatuses: [],
    documentTypes: [], // ✅ NEW: Document types cache
    dataVersion: 0,
  });
  
  // Enrollment state: endeavorId → { role, status, enrolledAt, enrollmentId }
  const [enrollmentsById, setEnrollmentsById] = useState({});
  // Progress overlay state: endeavorId → { assignments: { [assignmentId]: progressDTO }, events: { [eventId]: progressDTO } }
  const [progressOverlays, setProgressOverlays] = useState({});

  // SWR cache layer (internal persistence/TTL)
  const [, setCache] = useState(new Map());
  const [loadingStates, setLoadingStates] = useState(new Map());
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);
  
  // Status for refresh metadata
  const [status] = useState({
    isHydrating: false,
    isRefreshing: false,
    lastUpdatedAt: null,
  });

  // Wizard draft store (single source of truth for Create Course wizard)
  const [createCourseDraft, setCreateCourseDraft] = useState({
    draftEvents: [],
    draftAssignments: [],
    // track syllabus-origin sourceIds that the user explicitly removed so re-processing won't re-add them
    removedSyllabusSourceIds: [],
  });

  // -------------------------------
  // Create Course Wizard draft CRUD (defined at top-level of provider)
  // -------------------------------
  const updateDraftEvent = useCallback((id, patch) => {
    console.log('[DataContext] updateDraftEvent called', { id, patch });
    // Normalize incoming patch: ensure startDatetime/endDatetime and date (YYYY-MM-DD)
    const normalizePatch = (p) => {
      const np = { ...p };
      if (np.start && !np.startDatetime) np.startDatetime = np.start;
      if (np.end && !np.endDatetime) np.endDatetime = np.end;
      // If we have startDatetime or start, derive a simple date string
      const candidate = np.startDatetime || np.start || np.date || '';
      if (candidate) {
        const m = String(candidate).match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) np.date = m[1];
      }
      return np;
    };

    const normalizedPatch = normalizePatch(patch || {});

    setCreateCourseDraft(prev => {
      const events = (prev.draftEvents || []);
      console.log('[DataContext] updateDraftEvent prev sample', events.slice(0,5).map(e => ({ id: e.id, date: e.date || e.start || e.startDatetime })));
      const exists = events.some(e => e.id === id);
      const next = {
        ...prev,
        draftEvents: exists ? events.map(e => (e.id === id ? { ...e, ...normalizedPatch } : e)) : [...events, { id, ...normalizedPatch }]
      };
      console.log('[DataContext] updateDraftEvent next sample', next.draftEvents.slice(0,5).map(e => ({ id: e.id, date: e.date || e.start || e.startDatetime })));
      return next;
    });
  }, []);

  const deleteDraftEvent = useCallback((id) => {
    setCreateCourseDraft(prev => {
      const prevEvents = (prev.draftEvents || []);
      const toDelete = prevEvents.find(e => e.id === id);
      const nextEvents = prevEvents.filter(e => e.id !== id);
      const removed = new Set(prev.removedSyllabusSourceIds || []);
      if (toDelete && toDelete.isFromSyllabus && (toDelete.sourceId || toDelete.eventId || toDelete.id)) {
        removed.add(toDelete.sourceId || toDelete.eventId || toDelete.id);
      }
      return {
        ...prev,
        draftEvents: nextEvents,
        removedSyllabusSourceIds: Array.from(removed)
      };
    });
  }, []);

  const updateDraftAssignment = useCallback((id, patch) => {
    console.log('[DataContext] updateDraftAssignment called', { id, patch });
    setCreateCourseDraft(prev => {
      const items = (prev.draftAssignments || []);
      console.log('[DataContext] updateDraftAssignment prev items sample', items.slice(0,5).map(a => ({ id: a.id, dueDate: a.dueDate || a.dueDatetime })));
      const exists = items.some(a => a.id === id);
      const next = {
        ...prev,
        draftAssignments: exists ? items.map(a => (a.id === id ? { ...a, ...patch } : a)) : [...items, { id, ...patch }]
      };
      console.log('[DataContext] updateDraftAssignment next items sample', next.draftAssignments.slice(0,5).map(a => ({ id: a.id, dueDate: a.dueDate || a.dueDatetime })));
      return next;
    });
  }, []);

  const deleteDraftAssignment = useCallback((id) => {
    setCreateCourseDraft(prev => {
      const prevAssignments = (prev.draftAssignments || []);
      const toDelete = prevAssignments.find(a => a.id === id);
      const nextAssignments = prevAssignments.filter(a => a.id !== id);
      const removed = new Set(prev.removedSyllabusSourceIds || []);
      if (toDelete && toDelete.isFromSyllabus && (toDelete.sourceId || toDelete.assignmentId || toDelete.id)) {
        removed.add(toDelete.sourceId || toDelete.assignmentId || toDelete.id);
      }
      return {
        ...prev,
        draftAssignments: nextAssignments,
        removedSyllabusSourceIds: Array.from(removed)
      };
    });
  }, []);

  // Create helpers for wizard drafts
  const addDraftEvent = useCallback((payload) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `tmp_${Date.now()}_${Math.floor(Math.random()*100000)}`;
    // Normalize calendar-friendly fields
    const item = { id, isFromSyllabus: false, ...payload };
    // Ensure startDatetime and endDatetime exist if date + time provided
    if (!item.startDatetime && item.start) item.startDatetime = item.start;
    if (!item.endDatetime && item.end) item.endDatetime = item.end;
    if (!item.startDatetime && item.date && item.startTime) {
      item.startDatetime = `${item.date}T${item.startTime}:00`;
    }
    if (!item.endDatetime && item.date && (item.endTime || item.startTime)) {
      item.endDatetime = `${item.date}T${(item.endTime || item.startTime)}:00`;
    }
    // Also set a simple date field (YYYY-MM-DD) for day grouping
    if (!item.date && item.startDatetime) {
      const m = String(item.startDatetime).match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) item.date = m[1];
    }

    setCreateCourseDraft(prev => {
      const next = { ...prev, draftEvents: [ ...(prev.draftEvents || []), item ] };
      try { console.log('[DataContext] draftEvents length', next.draftEvents.length, next.draftEvents[next.draftEvents.length-1]); } catch (e) {}
      return next;
    });
    return item;
  }, []);

  const addDraftAssignment = useCallback((payload) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `tmp_${Date.now()}_${Math.floor(Math.random()*100000)}`;
    const item = { id, isFromSyllabus: false, ...payload };
    // Ensure dueDate exists as YYYY-MM-DD and dueDatetime if needed
    if (!item.dueDate && item.dueDatetime) {
      const m = String(item.dueDatetime).match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) item.dueDate = m[1];
    }
    if (!item.dueDatetime && item.dueDate) {
      item.dueDatetime = `${item.dueDate}T00:00:00`;
    }

    setCreateCourseDraft(prev => {
      const next = { ...prev, draftAssignments: [ ...(prev.draftAssignments || []), item ] };
      try { console.log('[DataContext] draftAssignments length', next.draftAssignments.length, next.draftAssignments[next.draftAssignments.length-1]); } catch (e) {}
      return next;
    });
    return item;
  }, []);
  
  // Refs for operational metadata (not rendered)
  const pendingWritesById = useRef({}); // { [id]: { seq, clientUpdatedAt, entityType } }
  const pendingPromisesRef = useRef(new Map());
  const mirrorCacheRef = useRef(new Map());
  const dashboardHydrationRef = useRef(null);
  const dashboardHydratedRef = useRef(false);
  const prevUserId = useRef(user?.id);

  // Handle user switch: clear canonical state when userId changes
  useEffect(() => {
    const currentUserId = user?.id;
    // If the user id changed and we either previously had a user OR
    // our canonical state already contains entities (e.g. from anonymous
    // browsing or cached responses), clear the state so the new user
    // does not inherit another user's data.
    const stateHasData = Boolean(
      Object.keys(state.assignmentsById || {}).length ||
      Object.keys(state.eventsById || {}).length ||
      Object.keys(state.documentsById || {}).length ||
      Object.keys(state.coursesById || {}).length
    );

    if (prevUserId.current !== currentUserId) {
      if (prevUserId.current != null || stateHasData) {
        console.log('[DataContext] User change detected, clearing canonical state');
        setState(prev => ({
          assignmentsById: {},
          eventsById: {},
          documentsById: {},
          coursesById: {},
          dataVersion: prev.dataVersion + 1,
        }));
        pendingWritesById.current = {};
        // Also clear cache mirrors so previously extracted entities are not re-used
        mirrorCacheRef.current = new Map();
        setCache(new Map());
        setEnrollmentsById({});
        setProgressOverlays({});
      }
    }

    prevUserId.current = currentUserId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Handle dev cache clear
  useEffect(() => {
    if (localStorage.getItem('dh.debug.clearCache') === '1') {
      console.log('[DataContext] Cache cleared via dev action');
      setState(prev => ({
        assignmentsById: {},
        eventsById: {},
        documentsById: {},
        coursesById: {},
        dataVersion: prev.dataVersion + 1,
      }));
      mirrorCacheRef.current = new Map();
      pendingWritesById.current = {};
      localStorage.removeItem('dh.debug.clearCache');
    }
  }, []);

  // Cache management functions
  const getCachedData = useCallback((key) => {
    // Only read from mirrorCacheRef for immediate/synchronous access
    // The cache state is async and may not reflect clearCache calls immediately
    return mirrorCacheRef.current.get(key);
  }, []);

  const setCachedData = useCallback((key, data) => {
    const entry = { data, timestamp: Date.now() };
    mirrorCacheRef.current.set(key, entry); // keep mirror in sync for immediate reads
    setCache(prev => new Map(prev.set(key, entry)));
    
    // Extract and normalize entities into canonical state
    if (data && typeof data === 'object') {
      const entities = {
        assignments: {},
        events: {},
        documents: {},
        courses: {},
      };
      
      // Helper to extract entities from arrays
      const extractFromArray = (arr, type) => {
        if (!Array.isArray(arr)) return;
        arr.forEach(item => {
          if (!item || typeof item !== 'object') return;
          let id = null;
          
          if (type === 'assignment') {
            id = normAssignmentId(item);
            if (id && !item.isDeleted) {
              const normalized = { ...item };
              const dueAt = normDueDate(item);
              const endeavorId = normEndeavorId(item);
              if (dueAt) normalized.dueAt = dueAt;
              if (endeavorId) normalized.endeavorId = endeavorId;
              entities.assignments[id] = normalized;
            }
          } else if (type === 'event') {
            id = normEventId(item);
            if (id && !item.isDeleted) {
              const normalized = { ...item };
              const startAt = normStartDate(item);
              const endeavorId = normEndeavorId(item);
              if (startAt) normalized.startAt = startAt;
              if (endeavorId) normalized.endeavorId = endeavorId;
              entities.events[id] = normalized;
            } else if (!id) {
              console.warn('[setCachedData] Event missing ID:', item);
            }
          } else if (type === 'document') {
            id = normDocumentId(item);
            if (id && !item.isDeleted) {
              entities.documents[id] = item;
            }
          } else if (type === 'course') {
            id = normEndeavorId(item);
            if (id) {
              entities.courses[id] = item;
            }
          }
        });
      };
      
      // Detect data type by cache key and extract entities
      if (Array.isArray(data)) {
        if (key.includes('assignment')) {
          console.log('[setCachedData] Extracting assignments from key:', key, 'count:', data.length);
          extractFromArray(data, 'assignment');
        }
        else if (key.includes('event')) {
          console.log('[setCachedData] Extracting events from key:', key, 'count:', data.length);
          extractFromArray(data, 'event');
        }
        // Be conservative when inferring documents: ignore keys that look like metadata/status/types lists
        else if (key.includes('document') && !key.toLowerCase().includes('status') && !key.toLowerCase().includes('type')) {
          console.log('[setCachedData] Extracting documents from key:', key, 'count:', data.length);
          extractFromArray(data, 'document');
        }
        else if (key.includes('endeavor') || key.includes('course')) extractFromArray(data, 'course');
      } else if (data.data && Array.isArray(data.data)) {
        // Paginated response
        if (key.includes('assignment')) {
          console.log('[setCachedData] Extracting assignments from paginated key:', key, 'count:', data.data.length);
          extractFromArray(data.data, 'assignment');
        }
        else if (key.includes('event')) {
          console.log('[setCachedData] Extracting events from paginated key:', key, 'count:', data.data.length);
          extractFromArray(data.data, 'event');
        }
        else if (key.includes('document') && !key.toLowerCase().includes('status') && !key.toLowerCase().includes('type')) {
          console.log('[setCachedData] Extracting documents from paginated key:', key, 'count:', data.data.length);
          extractFromArray(data.data, 'document');
        }
        else if (key.includes('endeavor') || key.includes('course')) extractFromArray(data.data, 'course');
      }
      
      // Update canonical state if we extracted any entities
      if (Object.keys(entities.assignments).length > 0 || 
          Object.keys(entities.events).length > 0 || 
          Object.keys(entities.documents).length > 0 ||
          Object.keys(entities.courses).length > 0) {
        
        // Debug logging for extraction
        if (Object.keys(entities.assignments).length > 0) {
          console.log('[setCachedData] Extracted', Object.keys(entities.assignments).length, 'assignments from key:', key);
        }
        if (Object.keys(entities.events).length > 0) {
          console.log('[setCachedData] Extracted', Object.keys(entities.events).length, 'events from key:', key);
        }
        
        setState(prev => ({
          assignmentsById: { ...prev.assignmentsById, ...entities.assignments },
          eventsById: { ...prev.eventsById, ...entities.events },
          documentsById: { ...prev.documentsById, ...entities.documents },
          coursesById: { ...prev.coursesById, ...entities.courses },
          dataVersion: prev.dataVersion + 1,
        }));
      }
    }
  }, []);

  const isLoading = useCallback((key) => {
    return loadingStates.get(key) || false;
  }, [loadingStates]);

  const setLoading = useCallback((key, loading) => {
    setLoadingStates(prev => new Map(prev.set(key, loading)));
  }, []);

  const generateCacheKey = useCallback((prefix, params = {}) => {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}-${params[key]}`)
      .join('_');
    return paramString ? `${prefix}_${paramString}` : prefix;
  }, []);

  // Await dashboard hydration if an in-flight bulk priming promise exists
  const awaitDashboardHydrationIfNeeded = useCallback(async (key) => {
    // If hydration is in progress, ALWAYS wait for it (regardless of current cache state)
    if (!dashboardHydratedRef.current && dashboardHydrationRef.current) {
      try {
        await dashboardHydrationRef.current;
      } catch (e) {
        // swallow hydration errors; individual fetch will proceed
        console.warn('[DataContext] ⚠️ Dashboard hydration failed or aborted; continuing with individual fetches');
      }
    }
  }, []);

  // === Nested entity ingestion & indexing ===
  // Walk arbitrary nested response trees (bulk endpoints, hydrated endeavors, etc.) and populate granular cache keys.
  // Heuristics: classify objects as endeavor/event/assignment based on presence of identifying fields.
  const ingestNestedEntities = useCallback((root) => {
    if (!root) return { counts: { endeavors:0, events:0, assignments:0 } };
    const endeavorsMap = new Map(); // endeavorId -> endeavor object (last seen)
    const eventsById = new Map(); // eventId -> event object
    const assignmentsById = new Map(); // assignmentId -> assignment object
    const eventsByEndeavor = new Map(); // endeavorId -> [events]
    const assignmentsByEndeavor = new Map(); // endeavorId -> [assignments]
    const assignmentsByEvent = new Map(); // eventId -> [assignments]

    const getFirst = (obj, keys) => {
      for (const k of keys) { if (obj && obj[k] != null) return obj[k]; }
      return null;
    };
    const normEndeavorId = (node) => getFirst(node, ['endeavorId','endeavorID','endeavor_id']);
    const normEventId = (node) => getFirst(node, ['eventId','eventID','event_id']);
    const normAssignmentId = (node) => getFirst(node, ['assignmentId','assignmentID','assignment_id']);

    const visit = (node) => {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) { node.forEach(visit); return; }
      // Descend first (breadth won't matter for classification)
      Object.keys(node).forEach(k => {
        const child = node[k];
        if (child && typeof child === 'object') {
          visit(child);
        }
      });
      // Classification heuristics
      const hasStart = ('startTime' in node) || ('startDate' in node) || ('eventDate' in node) || ('startDatetime' in node) || ('start_date' in node);
      const hasDue = ('dueDate' in node) || ('dueDatetime' in node) || ('due_at' in node) || ('dueDateTime' in node) || ('dueStart' in node) || ('dueEnd' in node);
      const endeavorIdVal = normEndeavorId(node);
      const eventIdVal = normEventId(node) ?? (hasStart && node.id != null ? node.id : null);
      const assignmentIdVal = normAssignmentId(node) ?? (hasDue && node.id != null ? node.id : null);
      const isAssignment = assignmentIdVal != null && (hasDue || node.assignmentId != null || node.assignmentID != null || node.assignment_id != null);
      const isEvent = eventIdVal != null && (hasStart || node.eventId != null || node.eventID != null || node.event_id != null);
      // Endeavor if it has an endeavorId and is not just an event/assignment referencing parent.
      const isEndeavor = (endeavorIdVal != null) && !isEvent && !isAssignment && (node.events || node.assignments || node.metadata || node.name);

      if (isEndeavor) {
        const eid = endeavorIdVal;
        endeavorsMap.set(eid, node);
        if (!eventsByEndeavor.has(eid)) eventsByEndeavor.set(eid, []);
        if (!assignmentsByEndeavor.has(eid)) assignmentsByEndeavor.set(eid, []);
      }
      if (isEvent) {
        const id = eventIdVal;
        eventsById.set(id, node);
        const endeavId = endeavorIdVal;
        if (endeavId != null) {
          if (!eventsByEndeavor.has(endeavId)) eventsByEndeavor.set(endeavId, []);
          eventsByEndeavor.get(endeavId).push(node);
        }
        // If nested assignments array present, ingest them too.
        const nestedAsn = node.assignments || node.deliverables || node.tasks;
        if (Array.isArray(nestedAsn) && nestedAsn.length) {
          nestedAsn.forEach(a => {
            if (a && typeof a === 'object') {
              const aId = normAssignmentId(a) ?? (a.id != null ? a.id : null);
              if (aId != null) {
                assignmentsById.set(aId, a);
                if (!assignmentsByEvent.has(id)) assignmentsByEvent.set(id, []);
                assignmentsByEvent.get(id).push(a);
                if (endeavId != null) {
                  if (!assignmentsByEndeavor.has(endeavId)) assignmentsByEndeavor.set(endeavId, []);
                  assignmentsByEndeavor.get(endeavId).push(a);
                }
              }
            }
          });
        }
      }
      if (isAssignment) {
        const id = assignmentIdVal;
        assignmentsById.set(id, node);
        // Link to event if present (support alt keys)
        const evId = normEventId(node);
        if (evId != null) {
          if (!assignmentsByEvent.has(evId)) assignmentsByEvent.set(evId, []);
          assignmentsByEvent.get(evId).push(node);
        }
        const eId = endeavorIdVal;
        if (eId != null) {
          if (!assignmentsByEndeavor.has(eId)) assignmentsByEndeavor.set(eId, []);
          assignmentsByEndeavor.get(eId).push(node);
        }
      }
    };
    visit(root);

    // Persist granular cache keys (idempotent overwrites ok)
    endeavorsMap.forEach((obj, id) => setCachedData(`endeavor_${id}`, obj));
    eventsById.forEach((obj, id) => setCachedData(`event_${id}`, obj));
    assignmentsById.forEach((obj, id) => setCachedData(`assignment_${id}`, obj));
    // Ensure every event has an event-assignments cache key (empty array signals no assignments, suppressing fetch churn)
    eventsById.forEach((_, id) => {
      if (!assignmentsByEvent.has(id)) {
        assignmentsByEvent.set(id, []);
      }
    });
    assignmentsByEvent.forEach((arr, id) => setCachedData(`event-assignments_${id}`, arr));
    eventsByEndeavor.forEach((arr, id) => { if (arr.length) setCachedData(`endeavor-events_${id}`, arr); });
    assignmentsByEndeavor.forEach((arr, id) => { if (arr.length) setCachedData(`endeavor-assignments_${id}`, arr); });

    // Build indices for fast in-memory lookup
    const assignmentIndex = {
      byId: Object.fromEntries(assignmentsById.entries()),
      byEventId: Object.fromEntries(assignmentsByEvent.entries()),
      byEndeavorId: Object.fromEntries(assignmentsByEndeavor.entries()),
      timestamp: Date.now()
    };
    const eventIndex = {
      byId: Object.fromEntries(eventsById.entries()),
      byEndeavorId: Object.fromEntries(eventsByEndeavor.entries()),
      timestamp: Date.now()
    };
    setCachedData('index_assignments', assignmentIndex);
    setCachedData('index_events', eventIndex);
    // Endeavor index (lightweight for potential future use)

    setCachedData('index_endeavors', { byId: Object.fromEntries(endeavorsMap.entries()), timestamp: Date.now() });

    return {
      counts: {
        endeavors: endeavorsMap.size,
        events: eventsById.size,
        assignments: assignmentsById.size
      }
    };
  }, [setCachedData]);

  const clearCache = useCallback((pattern) => {
    if (pattern) {
      const clearedKeys = [];
      setCache(prev => {
        const newCache = new Map();
        for (const [key, value] of prev.entries()) {
          if (!key.includes(pattern)) {
            newCache.set(key, value);
          } else {
            clearedKeys.push(key);
          }
        }
        return newCache;
      });

      // Also clear from mirrorCacheRef for immediate effect
      for (const key of Array.from(mirrorCacheRef.current.keys())) {
        if (key.includes(pattern)) {
          mirrorCacheRef.current.delete(key);
          if (!clearedKeys.includes(key)) clearedKeys.push(key);
        }
      }

      console.log(`[clearCache] 🗑️ Cleared ${clearedKeys.length} cache entries matching pattern "${pattern}":`, clearedKeys);

      // Automatically reset dashboard hydration when clearing the marker or core data
      if (pattern === 'dashboard_full_hydration') {
        dashboardHydratedRef.current = false;
        dashboardHydrationRef.current = null;
      }

      // Safety net: Reset dashboard hydration if core aggregated caches are cleared
      // This prevents inconsistent state where dashboardHydratedRef is true but cache is empty
      if (pattern === 'endeavors' || pattern.includes('endeavors_user-') ||
          pattern === 'user-events' || pattern === 'user-assignments') {
        console.log(`[clearCache] 🔄 Resetting dashboard hydration due to core data cache clear (pattern: ${pattern})`);
        dashboardHydratedRef.current = false;
        dashboardHydrationRef.current = null;
      }

      // Trigger refresh for trophy counts when relevant data changes
      if (pattern.includes('assignment') || pattern.includes('event') || pattern.includes('document')) {
        setDataRefreshTrigger(prev => {
          const newValue = prev + 1;
          console.log(`[clearCache] 📊 Incrementing dataRefreshTrigger: ${prev} → ${newValue}`);
          return newValue;
        });
      }
    } else {
      setCache(new Map());
      mirrorCacheRef.current.clear();
      dashboardHydratedRef.current = false;
      dashboardHydrationRef.current = null;
      setDataRefreshTrigger(prev => {
        const newValue = prev + 1;
        console.log(`[clearCache] 📊 Incrementing dataRefreshTrigger (clear all): ${prev} → ${newValue}`);
        return newValue;
      });
    }
  }, []);

  // Bulk dashboard hydrator: attempts single endpoint; falls back to composed parallel calls.
  const hydrateDashboard = useCallback(async (userId, options = {}) => {
    // Check if we have a recent /full cache (less than 2 hours old)
    const fullCacheKey = 'dashboard_full_hydration';
    const existingCache = getCachedData(fullCacheKey);
    const isCacheFresh = existingCache && (Date.now() - existingCache.timestamp < FULL_CACHE_TIMEOUT);
    
    if (dashboardHydratedRef.current && isCacheFresh) {
      console.log('[DataContext] Using fresh /full cache (age:', Math.round((Date.now() - existingCache.timestamp) / 1000 / 60), 'minutes)');
      return { status: 'already', hydrated: true };
    }
    
    // If cache is stale, clear all aggregated caches to force re-fetch
    if (!isCacheFresh && dashboardHydratedRef.current) {
      console.log('[DataContext] /full cache is stale, clearing aggregated caches and re-hydrating...');
      const userId = user?.id || user?.userId;
      if (userId) {
        clearCache(`endeavors_user-${userId}`);
      }
      clearCache('user-events');
      clearCache('user-assignments');
      clearCache('dashboard_full_hydration');
      dashboardHydratedRef.current = false;
      dashboardHydrationRef.current = null;
    }
    
    if (dashboardHydrationRef.current) {
      await dashboardHydrationRef.current; // wait existing run
      return { status: 'waiting-existing', hydrated: dashboardHydratedRef.current };
    }
    const { includeAssignments = true, includeEvents = true, assignmentPageSize = 500, eventPageSize = 250 } = options;
    const promise = (async () => {
      try {
        let rootPayload = null;
        
        // ✅ NEW: Try the /full endpoint first (returns HydratedEndeavorDTO[])
        try {
          const fullResp = await EndeavorApi.getEndeavorsByUserFull(userId);
          if (fullResp.ok) {
            const hydratedEndeavors = await fullResp.json();
            
            // Transform the fully hydrated response into our expected structure
            // ✅ CRITICAL: Also fetch ALL user documents (not just syllabuses)
            let allUserDocuments = [];
            try {
              const { getAudioDocumentTypeIds } = await import('../utils/documentTypeUtils');
              const audioTypeIds = await getAudioDocumentTypeIds();
              const userDocsResp = await DocumentApi.getDocumentsByUser(userId, { 
                page: 0, 
                size: 100, 
                sort: 'createdAt,desc',
                excludeDocumentTypeIds: audioTypeIds
              });
              const userDocsData = await (userDocsResp.ok ? userDocsResp.json() : userDocsResp);
              allUserDocuments = Array.isArray(userDocsData) ? userDocsData : (userDocsData?.content || []);
            } catch (e) {
              console.warn('[DataContext] Failed to fetch user documents after /full', e);
            }
            
            rootPayload = {
              endeavors: hydratedEndeavors,
              // Extract nested events and assignments for aggregated caching
              userEvents: hydratedEndeavors.flatMap(e => e.events || []),
              userAssignments: hydratedEndeavors.flatMap(e => [
                ...(e.assignments || []),
                ...(e.events || []).flatMap(ev => ev.assignments || [])
              ]),
              // Merge syllabus documents with all user documents
              userDocuments: [
                ...hydratedEndeavors
                  .filter(e => e.syllabusDocument)
                  .map(e => ({
                    ...e.syllabusDocument,
                    endeavorId: e.endeavorId // Ensure endeavor linkage
                  })),
                ...allUserDocuments
              ]
            };
            
            console.log('[DataContext] ✅ Transformed /full response', {
              endeavorsCount: rootPayload.endeavors.length,
              userEventsCount: rootPayload.userEvents.length,
              userAssignmentsCount: rootPayload.userAssignments.length,
              userDocumentsCount: rootPayload.userDocuments.length,
              userDocumentsSample: rootPayload.userDocuments[0]
            });
          } else {
            console.warn('[DataContext] /full endpoint non-200; falling back', fullResp.status);
          }
        } catch (e) {
          console.warn('[DataContext] /full endpoint fetch failed; falling back', e);
        }
        
        // Fallback to composed parallel calls if /full failed
        if (!rootPayload) {
          // Fallback composition: fetch raw responses then parse them before ingestion.
          const endeavorsResp = await EndeavorApi.getEndeavorsByUser(userId);
          const userEventsResp = includeEvents ? await EventApi.getUserEventsFiltered({ page:0, size: eventPageSize }) : null;
          const userAssignmentsResp = includeAssignments ? await AssignmentApi.getUserAssignmentsFiltered({ completed:false, page:0, size: assignmentPageSize }) : null;
          // ✅ PHASE 2: Fetch user's documents in parallel for dashboard enrichment
          const { getAudioDocumentTypeIds } = await import('../utils/documentTypeUtils');
          const audioTypeIds = await getAudioDocumentTypeIds();
          const userDocumentsResp = await DocumentApi.getDocumentsByUser(userId, {
            page: 0,
            size: 100, // reasonable limit for most users
            sort: 'createdAt,desc',
            excludeDocumentTypeIds: audioTypeIds
          });
          const parseJson = async (resp, label) => {
            if (!resp) return null;
            try {
              if (typeof resp.ok !== 'undefined') {
                if (!resp.ok) {
                  console.warn(`[DataContext] ${label} response not OK`, resp.status);
                  return null;
                }
                return await resp.json();
              }
              return resp; // already parsed object/array
            } catch (e) {
              console.warn(`[DataContext] Failed to parse ${label} JSON`, e);
              return null;
            }
          };
          const endeavorsData = await parseJson(endeavorsResp, 'endeavors');
          const userEventsData = await parseJson(userEventsResp, 'userEvents');
          const userAssignmentsData = await parseJson(userAssignmentsResp, 'userAssignments');
          const userDocumentsData = await parseJson(userDocumentsResp, 'userDocuments');
          rootPayload = { endeavors: endeavorsData, userEvents: userEventsData, userAssignments: userAssignmentsData, userDocuments: userDocumentsData };
          console.log('[DataContext] ✅ Composed & parsed dashboard payload (fallback)', {
            endeavorsCount: Array.isArray(endeavorsData) ? endeavorsData.length : (endeavorsData?.content?.length || 0),
            userEventsCount: Array.isArray(userEventsData) ? userEventsData.length : (userEventsData?.content?.length || 0),
            userAssignmentsCount: Array.isArray(userAssignmentsData) ? userAssignmentsData.length : (userAssignmentsData?.content?.length || 0),
            userDocumentsCount: Array.isArray(userDocumentsData) ? userDocumentsData.length : (userDocumentsData?.content?.length || 0)
          });
        }
        // Ingest nested entities (will populate indices & granular keys)
        ingestNestedEntities(rootPayload);
        // Synthesize aggregated user endeavors list cache key so overview components can adopt immediately without re-fetch.
        try {
          const endeavorsRaw = rootPayload.endeavors;
          const endeavorsList = Array.isArray(endeavorsRaw) ? endeavorsRaw : (endeavorsRaw?.content || []);
          if (Array.isArray(endeavorsList) && endeavorsList.length) {
            setCachedData(`endeavors_user-${userId}`, endeavorsList);
          }
        } catch (e) {
          console.warn('[DataContext] Failed to synthesize endeavors_user cache during hydration', e);
        }
        // ✅ Cache ALL user events with simple key (no date filters) - components filter client-side
        try {
          const eventsRaw = rootPayload.userEvents;
          const eventsList = Array.isArray(eventsRaw) ? eventsRaw : (eventsRaw?.content || []);
          if (Array.isArray(eventsList)) {
            // Cache ALL events with large page size
            const eventsKey = generateCacheKey('user-events', { page: 0, size: 10000 });
            setCachedData(eventsKey, eventsList);
            console.log('[DataContext] 📦 Cached', eventsList.length, 'events with key:', eventsKey);
          }
        } catch(e) { console.warn('[DataContext] Failed to synthesize user-events cache during hydration', e); }
        // ✅ Cache ALL user assignments with simple key (no date filters) - components filter client-side
        try {
          const asnRaw = rootPayload.userAssignments;
          const asnList = Array.isArray(asnRaw) ? asnRaw : (asnRaw?.content || []);
          if (Array.isArray(asnList)) {
            // Cache ALL assignments with large page size
            const asnKey = generateCacheKey('user-assignments', { page: 0, size: 10000 });
            setCachedData(asnKey, asnList);
            console.log('[DataContext] 📦 Cached', asnList.length, 'assignments with key:', asnKey);
          }
        } catch(e) { console.warn('[DataContext] Failed to synthesize user-assignments cache during hydration', e); }
        // Prepopulate per-endeavor user-assignments keys for common dashboard CourseCard queries (noEvent=true, completed=false, page=0, size=6)
        try {
          const asnRaw = rootPayload.userAssignments;
          const asnList = Array.isArray(asnRaw) ? asnRaw : (asnRaw?.content || []);
          if (Array.isArray(asnList) && asnList.length) {
            // Group by endeavorId while filtering for eventless + incomplete assignments
            const perEndeavor = new Map();
            asnList.forEach(a => {
              if (!a || typeof a !== 'object') return;
              const endeavorId = a.endeavorId || a.endeavorID || a.endeavor_id;
              if (endeavorId == null) return;
              const eventId = a.eventId || a.eventID || a.event_id;
              // Only include eventless when noEvent=true queries expected
              if (eventId != null) return;
              const isCompleted = a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed';
              if (isCompleted) return; // completed=false filter
              if (!perEndeavor.has(endeavorId)) perEndeavor.set(endeavorId, []);
              perEndeavor.get(endeavorId).push(a);
            });
            perEndeavor.forEach((arr, endeavorId) => {
              // Sort earliest due first (stable heuristic)
              const sorted = arr.slice().sort((a,b) => {
                const getDue = x => x.dueDatetime || x.dueDateTime || x.dueDate || x.due_at || x.dateDue;
                const da = new Date(getDue(a) || '').getTime();
                const db = new Date(getDue(b) || '').getTime();
                if (isNaN(da) && isNaN(db)) return 0; if (isNaN(da)) return 1; if (isNaN(db)) return -1; return da - db;
              });
              const subset = sorted.slice(0, 6);
              const key = generateCacheKey('user-assignments', { completed:false, endeavorId, noEvent:true, page:0, size:6 });
              // Only seed if not already present (avoid overwriting user-initiated refreshes or later larger windows)
              if (!getCachedData(key)) {
                setCachedData(key, subset);
              }
            });
          }
        } catch (e) {
          console.warn('[DataContext] Failed to prepopulate endeavor-specific user-assignments keys', e);
        }
        // ✅ UPDATED: Cache documents with granular keys, handling both /full and fallback responses
        try {
          const endeavorsRaw = rootPayload.endeavors;
          const endeavorsList = Array.isArray(endeavorsRaw) ? endeavorsRaw : (endeavorsRaw?.content || []);
          
          // When using /full endpoint, syllabusDocument is embedded in each endeavor
          // Build documents list from both embedded syllabusDocuments and userDocuments array
          const allDocs = [];
          const byEndeavor = new Map();
          
          // First, process embedded syllabus documents from endeavors (from /full endpoint)
          if (Array.isArray(endeavorsList)) {
            endeavorsList.forEach(endeavor => {
              const endeavorId = endeavor.endeavorId || endeavor.id;
              if (endeavorId && endeavor.syllabusDocument) {
                const syllabusDoc = {
                  ...endeavor.syllabusDocument,
                  endeavorId: endeavorId // Ensure endeavor linkage
                };
                allDocs.push(syllabusDoc);
                
                if (!byEndeavor.has(endeavorId)) byEndeavor.set(endeavorId, []);
                byEndeavor.get(endeavorId).push(syllabusDoc);
                
                // Cache individual syllabus document by ID
                const docId = syllabusDoc.id || syllabusDoc.documentId;
                if (docId) setCachedData(`document_${docId}`, syllabusDoc);
                
                // ✅ CRITICAL: Cache the exact query pattern Dashboard.js uses
                const syllabusKey = generateCacheKey('documents_user', {
                  userId,
                  endeavorId,
                  name: 'syllabus',
                  page: 0,
                  size: 1,
                  sort: 'createdAt,desc'
                });
                setCachedData(syllabusKey, [syllabusDoc]);
              } else if (endeavorId) {
                // No syllabus for this endeavor - cache empty array to prevent lookups
                const syllabusKey = generateCacheKey('documents_user', {
                  userId,
                  endeavorId,
                  name: 'syllabus',
                  page: 0,
                  size: 1,
                  sort: 'createdAt,desc'
                });
                if (!getCachedData(syllabusKey)) {
                  setCachedData(syllabusKey, []);
                }
              }
            });
          }
          
          // Then, process any additional documents from userDocuments array (fallback mode)
          const docsRaw = rootPayload.userDocuments;
          const docsList = Array.isArray(docsRaw) ? docsRaw : (docsRaw?.content || []);
          
          if (Array.isArray(docsList) && docsList.length) {
            docsList.forEach(doc => {
              allDocs.push(doc);
              
              // Cache individual documents by ID
              const docId = doc.documentId || doc.id;
              if (docId) setCachedData(`document_${docId}`, doc);
              
              // Group by endeavor
              const endeavorId = doc.endeavorId || doc.endeavorID || doc.endeavor_id;
              if (endeavorId) {
                if (!byEndeavor.has(endeavorId)) byEndeavor.set(endeavorId, []);
                byEndeavor.get(endeavorId).push(doc);
              }
            });
          }
          
          // Cache full documents list if we have any
          if (allDocs.length) {
            const docsKey = generateCacheKey('documents_user', { userId, page: 0, size: 100, sort: 'createdAt,desc' });
            console.log('[DataContext] Caching all documents:', { count: allDocs.length, key: docsKey, sample: allDocs[0] });
            setCachedData(docsKey, allDocs);
          }
          
          // Cache per-endeavor document lists (for fallback mode or additional docs)
          byEndeavor.forEach((docs, endeavorId) => {
            // Cache syllabus-specific query to match Dashboard.js exact pattern
            const syllabusKey = generateCacheKey('documents_user', {
              userId,
              endeavorId,
              name: 'syllabus',
              page: 0,
              size: 1,
              sort: 'createdAt,desc'
            });
            
            // Only cache if not already cached (prefer embedded syllabusDocument from /full)
            if (!getCachedData(syllabusKey)) {
              const syllabusDocs = docs.filter(d => 
                (d.name || d.documentTypeName || d.typeName || '').toLowerCase().includes('syllabus')
              ).slice(0, 1);
              
              setCachedData(syllabusKey, syllabusDocs.length ? syllabusDocs : []);
            }
            
            // Cache general endeavor documents query
            const endeavorKey = generateCacheKey('documents_endeavor', { endeavorId });
            setCachedData(endeavorKey, docs);
          });
          
          console.log(`[DataContext] 📄 Cached ${allDocs.length} documents with endeavor-specific keys`);
        } catch (e) {
          console.warn('[DataContext] Failed to cache documents during hydration', e);
        }
        // Mark hydration timestamp for staleness checking
        setCachedData('dashboard_full_hydration', { timestamp: Date.now() });
        dashboardHydratedRef.current = true;
        return { status: 'hydrated', hydrated: true };
      } finally {
        // keep promise reference for late awaiters; do not null immediately to allow gating until fully done
      }
    })();
    dashboardHydrationRef.current = promise;
    return promise;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestNestedEntities, setCachedData, generateCacheKey, getCachedData]);

  // clearCache moved above (needs to exist before hydrateDashboard)

  // Targeted invalidation helpers (narrow patterns improve retention of unrelated data)
  const invalidateUserAssignmentsCache = useCallback((predicate) => {
    // predicate optional: function(key) => boolean; default removes all user-assignments keys
    setCache(prev => {
      const next = new Map();
      for (const [key, value] of prev.entries()) {
        if (key.startsWith('user-assignments')) {
          // If predicate supplied and returns false, keep it; otherwise drop
          if (predicate && predicate(key) === false) {
            next.set(key, value);
          } else {
            // drop
          }
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }, []);

  // ✅ CORE: Universal fetch function with smart caching and deduplication
  const fetchWithCache = useCallback(async (key, fetchFn, cacheTime = 5 * 60 * 1000) => {
    // 1. Check if data is cached and still valid
    const cached = getCachedData(key);
    if (cached && (Date.now() - cached.timestamp < cacheTime)) {
      logDebug(`✅ Using cached data for ${key}`);
      return cached.data;
    }

    // 2. Check if request is already in progress - return the same promise
    const existingPromise = pendingPromisesRef.current.get(key);
    if (existingPromise) {
      logDebug(`⏳ Waiting for existing request: ${key}`);
      return existingPromise;
    }

    // 3. Start new request
    logInfo(`🔄 Starting new request: ${key}`);
    setLoading(key, true);

    // Create the actual request promise
    const requestPromise = (async () => {
      try {
        const response = await fetchFn();
        
        if (!response) {
          throw new Error('No response received from server');
        }

        let data;
        
        // Handle both Response objects and pre-parsed data
        if (typeof response.ok !== 'undefined') {
          // It's a Response object
          if (response.ok) {
            try {
              data = await response.json();
            } catch (jsonError) {
              console.error(`Failed to parse JSON for ${key}:`, jsonError);
              throw new Error(`Invalid JSON response: ${jsonError.message}`);
            }
          } else {
            let errorText = 'Unknown error';
            try {
              errorText = await response.text();
            } catch (textError) {
              errorText = `HTTP ${response.status}`;
            }
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }
        } else {
          // It's already parsed data
          data = response;
        }

        // DEV SHAPE LOGGING: attempt to summarize data shape for debugging empty UI lists
        if (process.env.NODE_ENV !== 'production') {
          try {
            let shapeSummary = {};
            if (Array.isArray(data)) {
              shapeSummary = { type: 'array', length: data.length };
              if (data.length > 0 && typeof data[0] === 'object') {
                shapeSummary.firstKeys = Object.keys(data[0]).slice(0, 15);
              }
            } else if (data && typeof data === 'object') {
              const topKeys = Object.keys(data);
              shapeSummary = { type: 'object', keys: topKeys.slice(0, 20) };
              // find first array inside object
              const firstArrayKey = topKeys.find(k => Array.isArray(data[k]));
              if (firstArrayKey) {
                shapeSummary.firstArrayKey = firstArrayKey;
                shapeSummary.firstArrayLen = data[firstArrayKey].length;
                if (data[firstArrayKey].length > 0 && typeof data[firstArrayKey][0] === 'object') {
                  shapeSummary.firstArrayItemKeys = Object.keys(data[firstArrayKey][0]).slice(0, 15);
                }
              }
              // nested 'content'/'data' introspection
              ['content','data','results','items','value'].forEach(k => {
                if (data[k] && Array.isArray(data[k])) {
                  shapeSummary[`arr_${k}`] = { len: data[k].length, itemKeys: (data[k][0] && typeof data[k][0]==='object') ? Object.keys(data[k][0]).slice(0,15) : undefined };
                } else if (data[k] && typeof data[k] === 'object') {
                  const nk = Object.keys(data[k]);
                  shapeSummary[`obj_${k}`] = { keys: nk.slice(0,15) };
                }
              });
            } else {
              shapeSummary = { type: typeof data };
            }
            logInfo(`✅ Successfully cached: ${key}`, { 
              dataSize: Array.isArray(data) ? data.length : typeof data,
              shape: shapeSummary
            });
          } catch (shapeErr) {
            console.warn('Shape logging failed for', key, shapeErr);
          }
        } else {
          logInfo(`✅ Successfully cached: ${key}`, { 
            dataSize: Array.isArray(data) ? data.length : typeof data 
          });
        }
        // Cache the successful response
        setCachedData(key, data);
        
        return data;
        
      } catch (error) {
        console.error(`❌ Error fetching ${key}:`, error);
        throw error;
      } finally {
        // Always clean up loading state and pending promise
        pendingPromisesRef.current.delete(key);
        setLoading(key, false);
      }
    })();

    // ✅ CRITICAL: Store the promise SYNCHRONOUSLY using ref
    pendingPromisesRef.current.set(key, requestPromise);

    return requestPromise;
  }, [getCachedData, setCachedData, setLoading]);

  /**
   * Stale-While-Revalidate variant of fetchWithCache.
   * Returns stale data immediately (if available) while kicking off a background
   * refresh.  An optional `onUpdate` callback fires when fresh data arrives so
   * the caller can silently replace its state.
   *
   * @param {string}   key        – Cache key
   * @param {Function} fetchFn    – () => Promise<Response|data>
   * @param {object}   opts
   * @param {number}   opts.cacheTime   – TTL in ms (default 5 min)
   * @param {Function} opts.onUpdate    – called with fresh data after revalidation
   * @returns {Promise<any>}  cached data (stale) or freshly fetched data
   */
  const fetchWithSWR = useCallback(async (key, fetchFn, { cacheTime = 5 * 60 * 1000, onUpdate } = {}) => {
    const cached = getCachedData(key);
    const isStale = cached && (Date.now() - cached.timestamp >= cacheTime);
    const isFresh = cached && !isStale;

    // Fresh cache → return as-is (same as fetchWithCache)
    if (isFresh) {
      logDebug(`✅ [SWR] Using fresh cached data for ${key}`);
      return cached.data;
    }

    // Stale cache → return immediately, then revalidate in background
    if (isStale) {
      logDebug(`♻️ [SWR] Returning stale data for ${key}, revalidating…`);

      // Fire-and-forget background revalidation (but reuse dedup if already in-flight)
      const existingPromise = pendingPromisesRef.current.get(key);
      if (!existingPromise) {
        const bgPromise = (async () => {
          try {
            const response = await fetchFn();
            let data;
            if (typeof response?.ok !== 'undefined') {
              if (response.ok) { data = await response.json(); } else { throw new Error(`HTTP ${response.status}`); }
            } else {
              data = response;
            }
            setCachedData(key, data);
            if (typeof onUpdate === 'function') onUpdate(data);
            logDebug(`✅ [SWR] Background revalidation complete for ${key}`);
          } catch (err) {
            console.warn(`[SWR] Background revalidation failed for ${key}:`, err?.message || err);
          } finally {
            pendingPromisesRef.current.delete(key);
          }
        })();
        pendingPromisesRef.current.set(key, bgPromise);
      }

      return cached.data; // stale but immediate
    }

    // No cache at all → fall through to normal fetch (blocking)
    return fetchWithCache(key, fetchFn, cacheTime);
  }, [getCachedData, setCachedData, fetchWithCache]);

  // Guard validateToken so it skips when no token; tighten cache window.
  const validateToken = useCallback(async () => {
    const tokenPresent = !!localStorage.getItem('access_token');
    if (!tokenPresent) throw new Error('NO_TOKEN');
    const key = 'validate-token';
    // Shorter cache (1 min) to reduce stale auth on rotation
    return fetchWithCache(key, () => AuthApi.validateToken(), 60 * 1000);
  }, [fetchWithCache]);

  // ✅ NEW: Check if user authentication is valid (cached)
  const isUserValid = useCallback(async () => {
    try {
      const userData = await validateToken();
      return userData ? true : false;
    } catch (error) {
      console.error('User validation failed:', error);
      return false;
    }
  }, [validateToken]);

  // ✅ NEW: Get current user data (cached)
  const getCurrentUser = useCallback(async () => {
    try {
      return await validateToken();
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }, [validateToken]);

  // ✅ NEW: Cached health check with 5-minute cache
  const checkHealth = useCallback(async (usePublic = false) => {
    const key = usePublic ? 'health-public' : 'health-auth';
    // Use 5-minute cache for health checks
    return fetchWithCache(key, () => {
      return usePublic ? AiHealthApi.checkHealthPublic() : AiHealthApi.checkHealth();
    }, 5 * 60 * 1000);
  }, [fetchWithCache]);

  // ✅ NEW: Check if AI service is available (cached)
  const isAiServiceAvailable = useCallback(async (usePublic = false) => {
    try {
      const healthData = await checkHealth(usePublic);
      return healthData ? true : false;
    } catch (error) {
      console.error('AI service health check failed:', error);
      return false;
    }
  }, [checkHealth]);

  // ✅ NEW: Get ecosystem health info (cached)
  const getEcosystemHealth = useCallback(async (usePublic = false) => {
    try {
      return await checkHealth(usePublic);
    } catch (error) {
      console.error('Error getting ecosystem health:', error);
      return null;
    }
  }, [checkHealth]);

  // ✅ FIXED: Now this useEffect can safely reference the auth methods defined above
  useEffect(() => {
    if (setDataContext) {
      setDataContext({
        validateToken,
        isUserValid,
        getCurrentUser,
        checkHealth,        // ✅ NEW: Expose health check
        isAiServiceAvailable, // ✅ NEW: Expose service availability check
        getEcosystemHealth, // ✅ NEW: Expose ecosystem health
        clearCache
      });
    }
  }, [setDataContext, validateToken, isUserValid, getCurrentUser, checkHealth, isAiServiceAvailable, getEcosystemHealth, clearCache]);

  // ✅ SMART: Individual API methods with automatic caching
  const getEndeavors = useCallback(async () => {
    const userId = user?.id || user?.userId;
    if (!userId) {
      throw new Error('User ID is required to fetch endeavors');
    }
    const response = await EndeavorApi.getEndeavors(userId);
    // Extract content array from paginated response
    if (response && typeof response === 'object' && 'content' in response) {
      return response.content;
    }
    // Return as-is if not paginated (backwards compatibility)
    return response;
  }, [user]);

  const getEndeavorsByUser = useCallback(async (userId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const key = `endeavors_user-${userId}`;
    // If dashboard already hydrated and aggregated user endeavors not yet cached, synthesize from index.
    if (dashboardHydratedRef.current) {
      const existingAgg = getCachedData(key);
      if (!existingAgg) {
        const idx = getCachedData('index_endeavors');
        if (idx?.data?.byId) {
          const synthesized = Object.values(idx.data.byId);
          if (synthesized.length) {
            setCachedData(key, synthesized);
            return synthesized;
          }
        }
      }
    }
    await awaitDashboardHydrationIfNeeded(key);
    const response = await fetchWithCache(key, () => EndeavorApi.getEndeavorsByUser(userId));
    // Extract content array from paginated response
    if (response && typeof response === 'object' && 'content' in response) {
      return response.content;
    }
    // Return as-is if not paginated (backwards compatibility)
    return response;
  }, [fetchWithCache, awaitDashboardHydrationIfNeeded, getCachedData, setCachedData]);

  const getEndeavor = useCallback(async (endeavorId) => {
    const key = `endeavor_${endeavorId}`;
    
    // First, await dashboard hydration if needed
    await awaitDashboardHydrationIfNeeded(key);
    
    // Check if we have cached data (either direct or from index)
    let existing = getCachedData(key);
    
    // Try to synthesize from index if not directly cached
    if (!existing) {
      const idx = getCachedData('index_endeavors');
      const obj = idx?.data?.byId?.[endeavorId];
      if (obj) {
        setCachedData(key, obj);
        return obj;
      }
    }
    
    // If we have cached data within the FULL_CACHE_TIMEOUT, use it
    if (existing && (Date.now() - existing.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached endeavor ${endeavorId} (age: ${Math.round((Date.now() - existing.timestamp) / 1000 / 60)} minutes)`);
      return existing.data;
    }
    
    // Otherwise, fetch from network
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => EndeavorApi.getEndeavor(endeavorId));
    // Only ingest if this was a NEW network fetch (skip if we just reused fresh cache)
    if (!wasFresh) {
      try { if (data && (data.events || data.assignments)) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, ingestNestedEntities, getCachedData, awaitDashboardHydrationIfNeeded, setCachedData]);

  const getEndeavorEvents = useCallback(async (endeavorId) => {
    const key = `endeavor-events_${endeavorId}`;
    
    // Await dashboard hydration first
    await awaitDashboardHydrationIfNeeded(key);
    
    // Check index first (populated by /full)
    const evIndex = getCachedData('index_events');
    if (evIndex?.data?.byEndeavorId?.[endeavorId] && (Date.now() - evIndex.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached events from index for endeavor ${endeavorId}`);
      return evIndex.data.byEndeavorId[endeavorId];
    }
    
    // Check direct cache
    let existing = getCachedData(key);
    if (!existing) {
      const idx = getCachedData('index_events');
      const list = idx?.data?.byEndeavorId?.[endeavorId];
      if (list) {
        setCachedData(key, list);
        existing = getCachedData(key);
      }
    }
    
    // Use cached data if within FULL_CACHE_TIMEOUT
    if (existing && (Date.now() - existing.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached endeavor-events ${endeavorId}`);
      return existing.data;
    }
    
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => EndeavorApi.getEndeavorEvents(endeavorId));
    if (!wasFresh) {
      try { if (Array.isArray(data) && data.length) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, getCachedData, ingestNestedEntities, awaitDashboardHydrationIfNeeded, setCachedData]);

  const getEndeavorAssignments = useCallback(async (endeavorId) => {
    const key = `endeavor-assignments_${endeavorId}`;
    const asnIndex = getCachedData('index_assignments');
    if (asnIndex?.data?.byEndeavorId?.[endeavorId]) {
      return asnIndex.data.byEndeavorId[endeavorId];
    }
    await awaitDashboardHydrationIfNeeded(key);
    const existing = getCachedData(key);
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => AssignmentApi.getAssignmentsByEndeavor(endeavorId));
    if (!wasFresh) {
      try { if (Array.isArray(data) && data.length) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, getCachedData, ingestNestedEntities, awaitDashboardHydrationIfNeeded]);

  // === New aggregated user-level fetchers (reduce N+1 calls) ===
  // Accept filter/pagination params that map directly to backend query params.
  // Cache key constructed from sorted param entries for stability.
  const getUserEvents = useCallback(async (params = {}) => {
    const key = generateCacheKey('user-events', params);
    await awaitDashboardHydrationIfNeeded(key);
    // Attempt synthesis from index for small, single-endeavor, first-page queries.
    try {
      const idx = getCachedData('index_events');
      if (idx?.data && params) {
  const { endeavorId, page = 0, size, status } = params; // status may be used for client-side filtering (upcoming/past)
        const singleEndeavor = endeavorId != null;
        const smallWindow = size != null && size <= 50;
        const firstPage = page === 0;
        if (singleEndeavor && firstPage && smallWindow) {
          const byEndeavor = idx.data.byEndeavorId?.[endeavorId];
          if (Array.isArray(byEndeavor)) {
            // Apply rudimentary status filtering if provided (e.g., upcoming/past) based on startTime/endTime fields.
            let filtered = byEndeavor;
            const now = Date.now();
            if (status === 'upcoming') {
              filtered = filtered.filter(ev => {
                const start = ev?.startTime || ev?.startDate || ev?.startsAt;
                return start ? new Date(start).getTime() >= now : true;
              });
            } else if (status === 'past') {
              filtered = filtered.filter(ev => {
                const end = ev?.endTime || ev?.endDate || ev?.endsAt || ev?.startTime || ev?.startDate;
                return end ? new Date(end).getTime() < now : false;
              });
            }
            if (size != null) filtered = filtered.slice(0, size);
            // Populate cache key to avoid future network call.
            setCachedData(key, filtered);
            return filtered;
          }
        }
      }
    } catch(e) { /* silent synthesis failure */ }
    return fetchWithCache(key, () => EventApi.getUserEventsFiltered(params));
  }, [fetchWithCache, generateCacheKey, awaitDashboardHydrationIfNeeded, getCachedData, setCachedData]);

  const getUserAssignments = useCallback(async (params = {}) => {
    const key = generateCacheKey('user-assignments', params);
    await awaitDashboardHydrationIfNeeded(key);
    // Synthesize small-window, single-endeavor, incomplete-only queries from index (avoid network).
    try {
      const idx = getCachedData('index_assignments');
      if (idx?.data && params) {
        const { endeavorId, page = 0, size, completed, status } = params;
        const singleEndeavor = endeavorId != null;
        const wantIncompleteOnly = completed === false || status === 'incomplete';
        const smallWindow = size != null && size <= 50; // heuristic threshold
        if (singleEndeavor && page === 0 && idx.data.byEndeavorId?.[endeavorId]) {
          let list = idx.data.byEndeavorId[endeavorId];
          if (wantIncompleteOnly) {
            list = list.filter(a => !(a.completed || a.status === 'Completed' || a.submissionStatus === 'Completed'));
          }
          if (smallWindow && size) list = list.slice(0, size);
          if (Array.isArray(list)) {
            setCachedData(key, list);
            return list;
          }
        }
      }
    } catch (e) {
      // silent fallback
    }
    return fetchWithCache(key, () => AssignmentApi.getUserAssignmentsFiltered(params));
  }, [fetchWithCache, generateCacheKey, awaitDashboardHydrationIfNeeded, getCachedData, setCachedData]);

  // ✅ NEW: Client-side filtering methods that work with cached unfiltered data
  const getFilteredEvents = useCallback(async (filters = {}) => {
    const {
      endeavorId,
      startDate,
      endDate,
      dateFilter, // 'upcoming' | 'past' | 'all'
    } = filters;

    // CHANGED: Get ALL cached events first to ensure cache is hydrated
    const allEventsKey = generateCacheKey('user-events', { page: 0, size: 10000 });
    // Skip dashboard hydration wait - we want fresh data after mutations
    // await awaitDashboardHydrationIfNeeded(allEventsKey);
    const cached = getCachedData(allEventsKey);
    
    // Only refetch if cache is truly stale or empty (optimistic updates keep it fresh)
    const CACHE_MAX_AGE = FULL_CACHE_TIMEOUT; // 2 hours - match hydration timeout
    const isCacheStale = !cached || (Date.now() - cached.timestamp > CACHE_MAX_AGE);
    const isCacheEmpty = !cached?.data || cached.data.length === 0;
    
    if (isCacheEmpty || isCacheStale) {
      try {
        const params = { page: 0, size: 10000 };
        const response = await EventApi.getUserEventsFiltered(params);
        const freshData = await response.json();
        const allEvents = Array.isArray(freshData) ? freshData : (freshData?.content || []);
        // Update cache with fresh data - this also populates canonical state
        if (response.ok && allEvents.length > 0) {
          setCachedData(allEventsKey, allEvents);
        }
      } catch (error) {
        console.error('[getFilteredEvents] Failed to fetch fresh data:', error);
      }
    }
    
    // CHANGED: Read from CACHE instead of state to avoid race condition
    // State may not be updated yet after setCachedData call
    const cachedData = getCachedData(allEventsKey);
    const allEventsFromCache = cachedData?.data || [];
    
    // Filter by endeavor if specified
    let filtered = allEventsFromCache.filter(e => {
      if (!e || e.isDeleted) return false;
      if (!endeavorId) return true;
      const eEndeavorId = normEndeavorId(e);
      return String(eEndeavorId) === String(endeavorId);
    });

    // Filter by date range
    const now = new Date();
    if (dateFilter === 'upcoming') {
      filtered = filtered.filter(event => {
        const eventDateStr = normStartDate(event);
        if (!eventDateStr) return false;
        const eventDate = new Date(eventDateStr);
        return !isNaN(eventDate.getTime()) && eventDate >= now;
      });
    } else if (dateFilter === 'past') {
      filtered = filtered.filter(event => {
        const eventDateStr = normStartDate(event);
        if (!eventDateStr) return false;
        const eventDate = new Date(eventDateStr);
        return !isNaN(eventDate.getTime()) && eventDate < now;
      });
    }

    // Filter by custom date range
    if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter(event => {
        const eventDateStr = normStartDate(event);
        if (!eventDateStr) return false;
        const eventDate = new Date(eventDateStr);
        return !isNaN(eventDate.getTime()) && eventDate >= start;
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      filtered = filtered.filter(event => {
        const eventDateStr = normStartDate(event);
        if (!eventDateStr) return false;
        const eventDate = new Date(eventDateStr);
        return !isNaN(eventDate.getTime()) && eventDate <= end;
      });
    }

    // Sort by date (ascending by default)
    filtered.sort((a, b) => {
      const dateA = new Date(normStartDate(a) || 0);
      const dateB = new Date(normStartDate(b) || 0);
      return dateA - dateB;
    });

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, generateCacheKey, awaitDashboardHydrationIfNeeded, getCachedData, setCachedData]);

  const getFilteredAssignments = useCallback(async (filters = {}) => {
    const {
      endeavorId,
      eventId,
      completed,
      dueStart,
      dueEnd,
      dateFilter, // 'upcoming' | 'past' | 'all'
      noEvent, // true to exclude event-linked assignments
    } = filters;

    // CHANGED: Get ALL cached assignments first to ensure cache is hydrated
    const allAssignmentsKey = generateCacheKey('user-assignments', { page: 0, size: 10000 });
    await awaitDashboardHydrationIfNeeded(allAssignmentsKey);
    const cached = getCachedData(allAssignmentsKey);
    
    // Only refetch if cache is truly stale or empty (optimistic updates keep it fresh)
    const CACHE_MAX_AGE = FULL_CACHE_TIMEOUT; // 2 hours - match hydration timeout
    const isCacheStale = !cached || (Date.now() - cached.timestamp > CACHE_MAX_AGE);
    const isCacheEmpty = !cached?.data || cached.data.length === 0;
    
    if (isCacheEmpty || isCacheStale) {
      try {
        const params = { page: 0, size: 10000 };
        console.log('[getFilteredAssignments] Fetching assignments from API with params:', params);
        const response = await AssignmentApi.getUserAssignmentsFiltered(params);
        const freshData = await response.json();
        const allAssignments = Array.isArray(freshData) ? freshData : (freshData?.content || []);
        console.log('[getFilteredAssignments] API returned:', allAssignments.length, 'assignments');
        if (allAssignments.length > 0) {
          console.log('[getFilteredAssignments] First assignment sample:', allAssignments[0]);
        }
        // Update cache with fresh data - this also populates canonical state
        if (response.ok && allAssignments.length > 0) {
          setCachedData(allAssignmentsKey, allAssignments);
          // Important: Return assignments directly since state update is async
          // This prevents a race condition where we read from state before it's updated
        } else if (allAssignments.length === 0) {
          console.warn('[getFilteredAssignments] ⚠️ API returned 0 assignments - you may need to create some assignments first');
        }
      } catch (error) {
        console.error('[getFilteredAssignments] Failed to fetch fresh data:', error);
      }
    } else {
      console.log('[getFilteredAssignments] Using cached assignments:', cached?.data?.length || 0);
    }
    
    // Get assignments from cache (which is synchronous) instead of state to avoid timing issues
    const cachedData = getCachedData(allAssignmentsKey);
    const allAssignmentsFromCache = cachedData?.data || [];
    
    // Filter by endeavor if specified
    let filtered = allAssignmentsFromCache.filter(a => {
      if (!a || a.isDeleted) return false;
      if (!endeavorId) return true; // No filter, return all non-deleted
      const aEndeavorId = normEndeavorId(a);
      return String(aEndeavorId) === String(endeavorId);
    });
    
    console.log('[getFilteredAssignments] Filtered from cache:', filtered.length, 'assignments (endeavorId:', endeavorId || 'all', ')');
    if (filtered.length > 0) {
      console.log('[getFilteredAssignments] First assignment date fields:', {
        dueDatetime: filtered[0].dueDatetime,
        dueDate: filtered[0].dueDate,
        dueAt: filtered[0].dueAt,
        normDueDate: normDueDate(filtered[0])
      });
    }


    // Filter by event
    if (eventId != null) {
      filtered = filtered.filter(assignment => {
        const eid = normEventId(assignment);
        return Number(eid) === Number(eventId);
      });
    }

    // Filter out event-linked assignments if noEvent=true
    if (noEvent === true) {
      filtered = filtered.filter(assignment => {
        const eid = normEventId(assignment);
        return eid == null;
      });
    }

    // Filter by completion status
    if (completed != null) {
      filtered = filtered.filter(assignment => {
        return completed ? selectors.isCompletedAssignment(assignment) : !selectors.isCompletedAssignment(assignment);
      });
    }

    // Filter by date range
    const now = new Date();
    if (dateFilter === 'upcoming') {
      filtered = filtered.filter(assignment => {
        const dueDateStr = normDueDate(assignment);
        if (!dueDateStr) return false;
        const dueDate = new Date(dueDateStr);
        return !isNaN(dueDate.getTime()) && dueDate >= now;
      });
    } else if (dateFilter === 'past') {
      filtered = filtered.filter(assignment => {
        const dueDateStr = normDueDate(assignment);
        if (!dueDateStr) return false;
        const dueDate = new Date(dueDateStr);
        return !isNaN(dueDate.getTime()) && dueDate < now;
      });
    }

    // Filter by custom date range
    if (dueStart) {
      const start = new Date(dueStart);
      console.log('[getFilteredAssignments] Filtering by dueStart:', dueStart, '(parsed:', start, ')');
      const beforeFilter = filtered.length;
      filtered = filtered.filter(assignment => {
        const dueDateStr = normDueDate(assignment);
        if (!dueDateStr) return false;
        const dueDate = new Date(dueDateStr);
        return !isNaN(dueDate.getTime()) && dueDate >= start;
      });
      console.log('[getFilteredAssignments] After dueStart filter:', filtered.length, 'of', beforeFilter, 'assignments remain');
      if (filtered.length > 0 && filtered.length < 5) {
        console.log('[getFilteredAssignments] Sample assignments after dueStart:', filtered.map(a => ({
          title: a.title,
          dueDate: normDueDate(a)
        })));
      }
    }
    if (dueEnd) {
      const end = new Date(dueEnd);
      console.log('[getFilteredAssignments] Filtering by dueEnd:', dueEnd, '(parsed:', end, ')');
      const beforeFilter = filtered.length;
      filtered = filtered.filter(assignment => {
        const dueDateStr = normDueDate(assignment);
        if (!dueDateStr) return false;
        const dueDate = new Date(dueDateStr);
        return !isNaN(dueDate.getTime()) && dueDate <= end;
      });
      console.log('[getFilteredAssignments] After dueEnd filter:', filtered.length, 'of', beforeFilter, 'assignments remain');
      if (filtered.length === 0 && beforeFilter > 0) {
        console.warn('[getFilteredAssignments] ⚠️ All assignments filtered out by dueEnd! Sample dates from before filter:');
        // Show what we had before
        const sampleBefore = Array.from({length: Math.min(5, beforeFilter)}, (_, i) => i).map(i => ({
          title: filtered[i]?.title || 'unknown',
          dueDate: normDueDate(filtered[i])
        }));
        console.log('[getFilteredAssignments] Would have been:', sampleBefore);
      }
    }

    // Sort by due date (ascending by default)
    filtered.sort((a, b) => {
      const dateA = new Date(normDueDate(a) || 0);
      const dateB = new Date(normDueDate(b) || 0);
      return dateA - dateB;
    });

    console.log('[getFilteredAssignments] Returning', filtered.length, 'assignments after all filters');
    if (filtered.length > 0) {
      console.log('[getFilteredAssignments] Date range of results:', {
        first: normDueDate(filtered[0]),
        last: normDueDate(filtered[filtered.length - 1])
      });
    }

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, generateCacheKey, awaitDashboardHydrationIfNeeded, getCachedData]);

  // Endeavors filtering (client-side from cache)
  const getFilteredEndeavors = useCallback(async (userId) => {
    // Get ALL cached endeavors
    if (!userId) return [];
    
    const allEndeavorsKey = `endeavors_user-${userId}`;
    
    // If dashboard already hydrated and aggregated user endeavors not yet cached, synthesize from index
    if (dashboardHydratedRef.current) {
      const existingAgg = getCachedData(allEndeavorsKey);
      if (!existingAgg) {
        const idx = getCachedData('index_endeavors');
        if (idx?.data?.byId) {
          const synthesized = Object.values(idx.data.byId);
          if (synthesized.length) {
            console.log('[getFilteredEndeavors] Synthesized', synthesized.length, 'endeavors from index');
            setCachedData(allEndeavorsKey, synthesized);
            return synthesized;
          }
        }
      }
    }
    
    await awaitDashboardHydrationIfNeeded(allEndeavorsKey);
    const cached = getCachedData(allEndeavorsKey);
    
    // getCachedData returns { data, timestamp }, we need the data property
    // data may be a plain array (from hydrateDashboard) or a paginated object { content: [...] } (from fetchWithCache)
    const rawData = cached?.data;
    const allEndeavors = Array.isArray(rawData) ? rawData : (rawData?.content || []);
    console.log('[getFilteredEndeavors] Retrieved', allEndeavors.length, 'endeavors from cache');
    
    // Return all endeavors (filtering can be added later if needed)
    return Array.isArray(allEndeavors) ? [...allEndeavors] : [];
  }, [awaitDashboardHydrationIfNeeded, getCachedData, setCachedData]);

  // Events
  const getEvents = useCallback(async () => {
    const key = 'events';
    return fetchWithCache(key, () => EventApi.getEvents());
  }, [fetchWithCache]);

  const getEvent = useCallback(async (eventId) => {
    const key = `event_${eventId}`;
    
    // Await dashboard hydration first
    await awaitDashboardHydrationIfNeeded(key);
    
    // Check index first (populated by /full)
    const evIndex = getCachedData('index_events');
    if (evIndex?.data?.byId?.[eventId] && (Date.now() - evIndex.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached event ${eventId} from index`);
      return evIndex.data.byId[eventId];
    }
    
    // Check direct cache
    const existing = getCachedData(key);
    if (existing && (Date.now() - existing.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached event ${eventId}`);
      return existing.data;
    }
    
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => EventApi.getEvent(eventId));
    if (!wasFresh) {
      try { if (data && data.assignments) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, getCachedData, ingestNestedEntities, awaitDashboardHydrationIfNeeded]);

  // Assignments associated with a specific event (cached per event)
  const getAssignmentsByEvent = useCallback(async (eventId) => {
    if (!eventId && eventId !== 0) throw new Error('eventId required for getAssignmentsByEvent');
    const key = `event-assignments_${eventId}`;
    
    // Await dashboard hydration first
    await awaitDashboardHydrationIfNeeded(key);
    
    // Check index first (populated by /full)
    const asnIndex = getCachedData('index_assignments');
    if (asnIndex?.data?.byEventId?.[eventId] && (Date.now() - asnIndex.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached assignments from index for event ${eventId}`);
      return asnIndex.data.byEventId[eventId];
    }
    
    // Check direct cache
    const existing = getCachedData(key);
    if (existing && (Date.now() - existing.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached event-assignments ${eventId}`);
      return existing.data;
    }
    
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => AssignmentApi.getAssignmentsByEvent(eventId));
    if (!wasFresh) {
      try { if (Array.isArray(data) && data.length) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, getCachedData, ingestNestedEntities, awaitDashboardHydrationIfNeeded]);

  // Assignments
  const getAssignments = useCallback(async () => {
    const key = 'assignments';
    return fetchWithCache(key, () => AssignmentApi.getAssignments());
  }, [fetchWithCache]);

  const getAssignment = useCallback(async (assignmentId) => {
    const key = `assignment_${assignmentId}`;
    
    // Await dashboard hydration first
    await awaitDashboardHydrationIfNeeded(key);
    
    // Check index first (populated by /full)
    const asnIndex = getCachedData('index_assignments');
    if (asnIndex?.data?.byId?.[assignmentId] && (Date.now() - asnIndex.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached assignment ${assignmentId} from index`);
      return asnIndex.data.byId[assignmentId];
    }
    
    // Check direct cache
    const existing = getCachedData(key);
    if (existing && (Date.now() - existing.timestamp < FULL_CACHE_TIMEOUT)) {
      logDebug(`✅ Using cached assignment ${assignmentId}`);
      return existing.data;
    }
    
    const wasFresh = existing && (Date.now() - existing.timestamp < 5 * 60 * 1000);
    const data = await fetchWithCache(key, () => AssignmentApi.getAssignment(assignmentId));
    if (!wasFresh) {
      try { if (data) ingestNestedEntities(data); } catch(e){ /* ignore */ }
    }
    return data;
  }, [fetchWithCache, getCachedData, ingestNestedEntities, awaitDashboardHydrationIfNeeded]);

  // Event Types
  const getEventTypes = useCallback(async () => {
    const key = 'event-types';
    return fetchWithCache(key, () => EventTypeApi.getEventTypes());
  }, [fetchWithCache]);

  const getEventType = useCallback(async (eventTypeId) => {
    const key = `event-type_${eventTypeId}`;
    return fetchWithCache(key, () => EventTypeApi.getEventType(eventTypeId));
  }, [fetchWithCache]);

  // ✅ SMART: Create/Update/Delete methods that invalidate relevant caches
  const createEndeavor = useCallback(async (data) => {
    const response = await EndeavorApi.createEndeavor(data);
    if (response && response.ok) {
      // Try to parse created object for optimistic cache update
      let created = null;
      try {
        created = await response.json();
      } catch (e) {
        // ignore parse errors (some APIs return 201 with no body)
      }

      // If we have the created endeavor, ingest and seed canonical + aggregated caches
      try {
        if (created) {
          // Populate per-endeavor cache
          const createdId = created.endeavorId || created.id || created.endeavorID;
          if (createdId) {
            setCachedData(`endeavor_${createdId}`, created);
          }

          // Ingest nested entities into indices for fast synthesis
          try { ingestNestedEntities(created); } catch (e) { /* swallow */ }

          // Seed aggregated user endeavors list so dashboard left-rail shows the new card
          const uid = user?.id || user?.userId;
          if (uid) {
            const aggKey = `endeavors_user-${uid}`;
            const existing = getCachedData(aggKey);
            const existingList = Array.isArray(existing?.data) ? existing.data : [];
            const deduped = [created, ...existingList.filter(e => {
              const id = e && (e.endeavorId || e.id || e.endeavorID);
              return String(id) !== String(createdId);
            })];
            setCachedData(aggKey, deduped);
          }
            }
          } catch (e) {
            console.warn('[createEndeavor] Failed to seed cache with created endeavor', e);
          }

          // Trigger an explicit refresh signal so UI lists recompute
          try {
            setDataRefreshTrigger(prev => prev + 1);
          } catch (e) {
            // ignore if setter not available for some reason
          }

          console.log('✅ Seeded aggregated endeavors cache after create (dashboard marker preserved for wizard control)');
    }
    return response;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCache, ingestNestedEntities, getCachedData, setCachedData, setDataRefreshTrigger, user]);

  const updateEndeavor = useCallback(async (endeavorId, data) => {
    const response = await EndeavorApi.updateEndeavor(endeavorId, data);
    if (response.ok) {
      // Invalidate related caches (major change - full refresh)
      const userId = user?.id || user?.userId;
      if (userId) {
        clearCache(`endeavors_user-${userId}`);
      }
      clearCache('endeavors');
      clearCache(`endeavor_${endeavorId}`);
      clearCache('dashboard_full_hydration'); // Auto-resets dashboardHydratedRef
      console.log(`🗑️ Cleared endeavor caches after update (major change): ${endeavorId}`);
    }
    return response;
  }, [clearCache, user]);

  const deleteEndeavor = useCallback(async (endeavorId) => {
    const response = await EndeavorApi.deleteEndeavor(endeavorId);
    if (response.ok) {
      // Invalidate related caches (major change - full refresh)
      const userId = user?.id || user?.userId;
      if (userId) {
        clearCache(`endeavors_user-${userId}`);
      }
      clearCache('endeavors');
      clearCache(`endeavor_${endeavorId}`);
      clearCache(`endeavor-events_${endeavorId}`);
      clearCache(`endeavor-assignments_${endeavorId}`);
      clearCache('dashboard_full_hydration'); // Auto-resets dashboardHydratedRef
      console.log(`🗑️ Cleared all caches for deleted endeavor (major change): ${endeavorId}`);
    }
    return response;
  }, [clearCache, user]);

  // === Optimistic patch + delete wrappers for endeavors ===

  const applyEndeavorPatchLocal = useCallback((prevState, endeavorId, patch) => {
    const id = String(endeavorId);
    const nextCourses = { ...(prevState.coursesById || {}) };
    const existing = nextCourses[id] || {};
    nextCourses[id] = { ...existing, ...patch };
    return {
      ...prevState,
      coursesById: nextCourses,
      dataVersion: (prevState.dataVersion || 0) + 1,
    };
  }, []);

  const patchEndeavor = useCallback(async (endeavorId, patch) => {
    const prevSnapshot = {
      assignmentsById: state.assignmentsById,
      eventsById: state.eventsById,
      documentsById: state.documentsById,
      coursesById: state.coursesById,
      dataVersion: state.dataVersion,
    };

    // Optimistic local merge
    setState(curr => applyEndeavorPatchLocal(curr, endeavorId, patch));

    try {
      const response = await EndeavorApi.patchEndeavor(endeavorId, patch);
      if (response && response.ok) {
        // If server returned canonical object, reconcile local state with it
        let updated = null;
        try { updated = await response.json(); } catch (_) { updated = null; }
        if (updated) {
          setState(curr => {
            const id = String(endeavorId);
            const nextCourses = { ...(curr.coursesById || {}) };
            nextCourses[id] = updated;
            return {
              ...curr,
              coursesById: nextCourses,
              dataVersion: (curr.dataVersion || 0) + 1,
            };
          });
        }
        // Cache hygiene
        if (typeof clearCache === 'function') {
          clearCache('endeavors');
          clearCache(`endeavor_${endeavorId}`);
          clearCache('dashboard_full_hydration'); // Auto-resets dashboardHydratedRef to maintain consistency
        }
        return response;
      } else {
        // Non-OK response -> rollback
        setState(prevSnapshot);
        throw new Error(`patchEndeavor failed (${response?.status || 'no-response'})`);
      }
    } catch (err) {
      // Rollback on any error
      setState(prevSnapshot);
      throw err;
    }
  }, [applyEndeavorPatchLocal, clearCache, state]);

  const removeEndeavor = useCallback(async (endeavorId) => {
    const prevSnapshot = {
      assignmentsById: state.assignmentsById,
      eventsById: state.eventsById,
      documentsById: state.documentsById,
      coursesById: state.coursesById,
      dataVersion: state.dataVersion,
    };

    // Optimistic removal from local canonical map
    setState(curr => {
      const id = String(endeavorId);
      const nextCourses = { ...(curr.coursesById || {}) };
      delete nextCourses[id];

      // Also purge assignments belonging to this course
      const nextAssignments = { ...(curr.assignmentsById || {}) };
      Object.keys(nextAssignments).forEach(key => {
        const a = nextAssignments[key];
        if (a && String(a.endeavorId || a.endeavorID || a.endeavor_id || a.courseId || a.course_id || '') === id) {
          delete nextAssignments[key];
        }
      });

      // Also purge events belonging to this course
      const nextEvents = { ...(curr.eventsById || {}) };
      Object.keys(nextEvents).forEach(key => {
        const e = nextEvents[key];
        if (e && String(e.endeavorId || e.endeavorID || e.endeavor_id || e.courseId || e.course_id || '') === id) {
          delete nextEvents[key];
        }
      });

      return {
        ...curr,
        coursesById: nextCourses,
        assignmentsById: nextAssignments,
        eventsById: nextEvents,
        dataVersion: (curr.dataVersion || 0) + 1,
      };
    });

    try {
      const response = await EndeavorApi.deleteEndeavor(endeavorId);
      if (response && response.ok) {
        // success -> clear caches (major change - full refresh)
        if (typeof clearCache === 'function') {
          const userId = user?.id || user?.userId;
          if (userId) {
            clearCache(`endeavors_user-${userId}`);
          }
          clearCache('endeavors');
          clearCache(`endeavor_${endeavorId}`);
          clearCache(`endeavor-events_${endeavorId}`);
          clearCache(`endeavor-assignments_${endeavorId}`);
          clearCache('user-events');
          clearCache('user-assignments');
          clearCache('dashboard_full_hydration'); // Auto-resets dashboardHydratedRef
        }
        // Notify hooks (academic health tiles, upcoming assessments, etc.)
        try {
          window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
          window.dispatchEvent(new CustomEvent('assignments:updated'));
        } catch (_) { /* */ }
        return response;
      }

      // If non-OK and indicates dependency constraint (e.g., 409), attempt cascade delete fallback once
      const status = response?.status;
      if (status === 409 || (response && typeof response.json === 'function')) {
        // try to read server message to confirm dependency failure
        let body = null;
        try { body = await response.json(); } catch (_) { body = null; }
        const msg = (body && (body.message || body.error || body.detail)) || '';
        const looksLikeDeps = status === 409 || /dependent|constraint|references exist|cannot delete/i.test(String(msg));
        if (looksLikeDeps) {
          // Locate dependents from local canonical state and call their delete endpoints
          try {
            const events = Object.values(state.eventsById || {}).filter(e => String(e.endeavorId) === String(endeavorId));
            const assignments = Object.values(state.assignmentsById || {}).filter(a => String(a.endeavorId) === String(endeavorId));
            const documents = Object.values(state.documentsById || {}).filter(d => String(d.endeavorId) === String(endeavorId));

            // Delete dependents in parallel (best-effort)
            await Promise.all(events.map(ev => {
              const eid = ev.id || ev.eventId || ev.eventID;
              return eid ? EventApi.deleteEvent(eid) : Promise.resolve();
            }));
            await Promise.all(assignments.map(as => {
              const aid = as.id || as.assignmentId || as.assignmentID;
              return aid ? AssignmentApi.deleteAssignment(aid) : Promise.resolve();
            }));
            await Promise.all(documents.map(doc => {
              const did = doc.id || doc.documentId || doc.documentID;
              return did ? DocumentApi.deleteDocument(did) : Promise.resolve();
            }));

            // Retry endeavor delete once
            const retry = await EndeavorApi.deleteEndeavor(endeavorId);
            if (retry && retry.ok) {
              if (typeof clearCache === 'function') {
                const userId = user?.id || user?.userId;
                if (userId) {
                  clearCache(`endeavors_user-${userId}`);
                }
                clearCache('endeavors');
                clearCache(`endeavor_${endeavorId}`);
                clearCache(`endeavor-events_${endeavorId}`);
                clearCache(`endeavor-assignments_${endeavorId}`);
                clearCache('user-events');
                clearCache('user-assignments');
                clearCache('dashboard_full_hydration'); // Auto-resets dashboardHydratedRef
              }
              // Notify hooks (academic health tiles, upcoming assessments, etc.)
              try {
                window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
                window.dispatchEvent(new CustomEvent('assignments:updated'));
              } catch (_) { /* */ }
              return retry;
            }
            // Retry failed -> rollback
            setState(prevSnapshot);
            throw new Error('Delete failed after cascade attempt');
          } catch (cascadeErr) {
            setState(prevSnapshot);
            throw cascadeErr;
          }
        }
      }

      // Non-dependency failure -> rollback
      setState(prevSnapshot);
      throw new Error(`deleteEndeavor failed (${status || 'no-response'})`);
    } catch (err) {
      // Rollback on network / unexpected errors
      setState(prevSnapshot);
      throw err;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCache, state]);

  const createEvent = useCallback(async (data) => {
    try {
      console.log('Creating event with data:', data);
      const response = await EventApi.createEvent(data);
      
      if (response.ok) {
        console.log('Event created successfully');
        const createdEvent = await response.json();
        
        // Normalize the created event (handles both single object and array responses)
        const normalized = normalizeEvent(createdEvent, data);
        
        // Upsert normalized event(s) into canonical state dictionary
        const eventsToUpsert = Array.isArray(normalized) ? normalized : [normalized];
        // Prepare container for any generated recurring series instances
        let generatedSeries = [];
        if (eventsToUpsert.length > 0 && eventsToUpsert[0]) {
          setState(prev => {
            const newEventsById = { ...prev.eventsById };
            eventsToUpsert.forEach(event => {
              if (event && event.eventId) {
                newEventsById[String(event.eventId)] = event;
              }
            });
            console.log(`✅ Upserted ${eventsToUpsert.length} event(s) into canonical state`);
            return {
              ...prev,
              eventsById: newEventsById,
              dataVersion: prev.dataVersion + 1
            };
          });
        }
        // Upsert any generated series instances into canonical state as well (if/when populated)
        if (generatedSeries && generatedSeries.length) {
          setState(prev => {
            const newEventsById = { ...prev.eventsById };
            generatedSeries.forEach(event => {
              if (event && (event.eventId || event.id)) {
                newEventsById[String(event.eventId || event.id)] = event;
              }
            });
            console.log(`✅ Upserted ${generatedSeries.length} generated series event(s) into canonical state`);
            return {
              ...prev,
              eventsById: newEventsById,
              dataVersion: prev.dataVersion + 1
            };
          });
        }
        
        // If this is a recurring event, generate the series and collect generated instances
        
        if (data.isRecurring && data.recurrenceRule) {
          const eventId = createdEvent.eventId || createdEvent.id;
          console.log('Generating recurring event series for event:', eventId);

          try {
            const seriesResponse = await EventApi.generateEventSeries(eventId);
            if (seriesResponse.ok) {
              // Try to parse returned series instances (backend may return expanded instances)
              try {
                const seriesBody = await seriesResponse.json();
                // Normalize whatever the backend returned (array or single)
                const normalizedSeries = normalizeEvent(seriesBody, data);
                if (normalizedSeries) {
                  generatedSeries = Array.isArray(normalizedSeries) ? normalizedSeries : [normalizedSeries];
                }
              } catch (parseErr) {
                // If there's no JSON body or parse failed, fall back to refreshing later
                console.warn('Could not parse generated series response body:', parseErr);
              }
              console.log('✅ Recurring event series generated successfully');
            } else {
              console.warn('⚠️ Failed to generate recurring series:', await seriesResponse.text());
            }
          } catch (seriesError) {
            console.error('Error generating recurring series:', seriesError);
            // Don't fail the whole operation if series generation fails
          }
        }

        // Update caches by adding new event(s) instead of clearing
        const eventsToAdd = Array.isArray(normalized) ? normalized : [normalized];
        // Append any generated series instances to the list of events to add
        if (generatedSeries && generatedSeries.length) {
          // Avoid duplicates by eventId
          const seen = new Set(eventsToAdd.map(e => String(e.eventId || e.id)));
          generatedSeries.forEach(e => {
            const id = String(e.eventId || e.id);
            if (!seen.has(id)) {
              eventsToAdd.push(e);
              seen.add(id);
            }
          });
        }
        const allEventsKey = generateCacheKey('user-events', { page: 0, size: 10000 });
        const cached = getCachedData(allEventsKey);
        if (cached && Array.isArray(cached.data)) {
          // Merge while deduplicating by eventId
          const existingById = new Map(cached.data.map(ev => [String(ev.eventId || ev.id), ev]));
          eventsToAdd.forEach(ev => existingById.set(String(ev.eventId || ev.id), ev));
          const updatedEvents = Array.from(existingById.values());
          setCachedData(allEventsKey, updatedEvents);
          console.log(`✅ Added ${eventsToAdd.length} event(s) to cache`);
        }
        
        // Return normalized event(s) for modal's use
        return normalized;
      } else {
        const errorText = await response.text();
        console.error('Failed to create event:', response.status, errorText);
        throw new Error(`Failed to create event: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error in createEvent:', error);
      throw error;
    }
  }, [generateCacheKey, getCachedData, setCachedData, setState]);

  const updateCachedEvent = useCallback((eventId, updatedFields) => {
    const keysToUpdate = [];
    
    // Find all cache keys that might contain this event
    const allKeys = Array.from(mirrorCacheRef.current.keys());
    
    allKeys.forEach(key => {
      if (
        key === `event_${eventId}` ||
        key.startsWith('events') ||
        key.startsWith('user-events') ||
        key.startsWith('endeavor-events_')
      ) {
        keysToUpdate.push(key);
      }
    });
    
    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (!cached) return;
      
      let updated = false;
      
      // Handle single event cache
      if (key === `event_${eventId}`) {
        const newData = { ...cached.data, ...updatedFields };
        setCachedData(key, newData);
        updated = true;
      }
      // Handle array of events
      else if (Array.isArray(cached.data)) {
        const newArray = cached.data.map(event => {
          const id = event?.eventId || event?.eventID || event?.event_id || event?.id;
          if (String(id) === String(eventId)) {
            return { ...event, ...updatedFields };
          }
          return event;
        });
        
        // Only update if we found and modified the event
        if (newArray.some((e, i) => {
          const id = e?.eventId || e?.eventID || e?.event_id || e?.id;
          return String(id) === String(eventId) && e !== cached.data[i];
        })) {
          setCachedData(key, newArray);
          updated = true;
        }
      }
      
      if (updated) {
        logDebug(`✏️ Optimistically updated event ${eventId} in cache: ${key}`);
      }
    });
  }, [getCachedData, setCachedData]);

  const updateEvent = useCallback(async (eventId, data) => {
    try {
      console.log('DataContext.updateEvent: outbound', { eventId, payload: data });
    } catch (e) { /* ignore stringify errors */ }

    // Store original state for rollback on failure
    const originalState = new Map();
    const keysToUpdate = Array.from(mirrorCacheRef.current.keys()).filter(key => 
      key === `event_${eventId}` ||
      key.startsWith('events') ||
      key.startsWith('user-events') ||
      key.startsWith('endeavor-events_')
    );
    
    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (cached) {
        originalState.set(key, JSON.parse(JSON.stringify(cached.data)));
      }
    });

    // Optimistically update cache before API call for instant UI feedback
    updateCachedEvent(eventId, data);
    
    // Trigger components to refetch with the optimistically updated data
    setDataRefreshTrigger(prev => prev + 1);

    try {
      // API now returns parsed object or null (for 204), or throws on errors
      const serverData = await EventApi.updateEvent(eventId, data);

      // If server returned updated entity, merge it into cache (authoritative writeback)
      if (serverData) {
        console.log('DataContext.updateEvent: success with server data', serverData);
        updateCachedEvent(eventId, serverData);
        setDataRefreshTrigger(prev => prev + 1);
        return { ok: true, data: serverData };
      } else {
        // 204 No Content - optimistic update remains
        console.log('DataContext.updateEvent: success (204 No Content), using optimistic update');
        return { ok: true, data: { ...data, eventId, id: eventId } };
      }
    } catch (error) {
      // Rollback optimistic update on ANY failure (network, HTTP error, parse error)
      console.error(`⚠️ Event update failed, rolling back optimistic changes for ${eventId}:`, error);
      originalState.forEach((data, key) => {
        setCachedData(key, data);
      });
      setDataRefreshTrigger(prev => prev + 1);
      throw error; // Re-throw for parent handler
    }
  }, [getCachedData, setCachedData, updateCachedEvent]);

  const deleteEvent = useCallback(async (eventId) => {
    const response = await EventApi.deleteEvent(eventId);
    if (response.ok) {
      // Update cache directly without triggering entity extraction
      const allEventsKey = `user-events_page-0_size-10000`;
      const cached = mirrorCacheRef.current.get(allEventsKey);
      if (cached && Array.isArray(cached.data)) {
        const updatedEvents = cached.data.filter(e => {
          const id = e.eventId || e.id;
          return String(id) !== String(eventId);
        });
        mirrorCacheRef.current.set(allEventsKey, { data: updatedEvents, timestamp: Date.now() });
      }
      
      // Update state with dataVersion increment but WITHOUT entity extraction
      setState(prev => {
        const newEventsById = { ...prev.eventsById };
        delete newEventsById[String(eventId)];
        return {
          ...prev,
          eventsById: newEventsById,
          dataVersion: prev.dataVersion + 1
        };
      });
      
      console.log(`🗑️ Deleted event: ${eventId}`);
    }
    return response;
  }, [setState]);

  const deleteEventSeries = useCallback(async (parentEventId) => {
    console.log(`🗑️ [deleteEventSeries] Called with ID: ${parentEventId}`);
    try {
      // First try the backend endpoint
      const response = await EventApi.deleteEventSeries(parentEventId);
      console.log(`🗑️ [deleteEventSeries] Response status: ${response.status}`);
      
      if (response.ok) {
        // Surgically remove series events from cache (optimistic update)
        console.log(`🗑️ [deleteEventSeries] Backend delete succeeded, removing from cache...`);
        
        // Update aggregated events cache
        const allEventsKey = 'user-events_page-0_size-10000';
        const cached = getCachedData(allEventsKey);
        if (cached && Array.isArray(cached.data)) {
          const filtered = cached.data.filter(e => 
            e.parentEventId !== parentEventId && 
            (e.eventId || e.id) !== parentEventId
          );
          setCachedData(allEventsKey, filtered);
          console.log(`🗑️ [deleteEventSeries] Removed ${cached.data.length - filtered.length} events from cache`);
        }
        
        // Update canonical state
        setState(prev => ({
          ...prev,
          eventsById: Object.fromEntries(
            Object.entries(prev.eventsById).filter(([id, e]) => 
              e.parentEventId !== parentEventId && id !== String(parentEventId)
            )
          ),
          dataVersion: prev.dataVersion + 1
        }));
        console.log(`🗑️ [deleteEventSeries] Cleared event caches after series delete: ${parentEventId}`);
        return response;
      }
      
      // If backend doesn't support it (404 or 500), fall back to client-side deletion
      const errorText = await response.text();
      console.log(`⚠️ [deleteEventSeries] Backend endpoint not available, using fallback. Error: ${errorText}`);
      
      // Get all events and find ones in this series
      const allEvents = await getUserEvents({ page: 0, size: 10000 });
      const seriesEvents = allEvents.filter(event => 
        event.parentEventId === parentEventId || 
        event.eventId === parentEventId ||
        event.id === parentEventId
      );
      
      console.log(`🗑️ [deleteEventSeries] Found ${seriesEvents.length} events to delete in series`, seriesEvents.map(e => e.eventId || e.id));
      
      // Delete all events in the series (note: backend may be broken but we'll try anyway)
      const deletePromises = seriesEvents.map(async event => {
        try {
          const eventId = event.eventId || event.id;
          const deleteResponse = await EventApi.deleteEvent(eventId);
          console.log(`🗑️ Delete event ${eventId}: ${deleteResponse.status}`);
          return deleteResponse;
        } catch (err) {
          console.error(`❌ Failed to delete event ${event.eventId || event.id}:`, err);
          return { ok: false };
        }
      });
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r.ok).length;
      console.log(`🗑️ [deleteEventSeries] Deleted ${successCount}/${seriesEvents.length} events`);
      
      // Surgically remove series events from cache (optimistic update)
      console.log(`🗑️ [deleteEventSeries] Removing series events from cache...`);
      
      // Update aggregated events cache
      const allEventsKey = 'user-events_page-0_size-10000';
      const cached = getCachedData(allEventsKey);
      if (cached && Array.isArray(cached.data)) {
        const filtered = cached.data.filter(e => 
          e.parentEventId !== parentEventId && 
          (e.eventId || e.id) !== parentEventId
        );
        setCachedData(allEventsKey, filtered);
        console.log(`🗑️ [deleteEventSeries] Removed ${cached.data.length - filtered.length} events from cache`);
      }
      
      // Clear individual event cache keys for deleted events
      seriesEvents.forEach(e => {
        const eid = e.eventId || e.id;
        if (eid) clearCache(`event_${eid}`);
      });
      
      // Update canonical state
      setState(prev => ({
        ...prev,
        eventsById: Object.fromEntries(
          Object.entries(prev.eventsById).filter(([id, e]) => 
            e.parentEventId !== parentEventId && id !== String(parentEventId)
          )
        ),
        dataVersion: prev.dataVersion + 1
      }));
      
      console.log(`🗑️ [deleteEventSeries] Surgical removal complete`);
      
      return { ok: true, status: 200 };
    } catch (error) {
      console.error(`❌ [deleteEventSeries] Error:`, error);
      throw error;
    }
  }, [clearCache, getUserEvents, getCachedData, setCachedData, setState]);

  /**
   * refreshCalendarRange - Refetch events and assignments for a specific date range
   * 
   * Used for targeted validation after optimistic updates in the calendar.
   * Only refetches the specified month range rather than the full 3-year window.
   * 
   * @param {string} startIso - Start date in ISO format (YYYY-MM-DD)
   * @param {string} endIso - End date in ISO format (YYYY-MM-DD)
   * @returns {Promise<{events: Array, assignments: Array}>}
   */
  const refreshCalendarRange = useCallback(async (startIso, endIso) => {
    if (!user?.id) {
      console.warn('[refreshCalendarRange] No user ID available');
      return { events: [], assignments: [] };
    }

    try {
      logInfo(`[refreshCalendarRange] Fetching range: ${startIso} to ${endIso}`);

      // Fetch events for the date range
      const eventsParams = {
        userId: user.id,
        startDate: startIso,
        endDate: endIso
      };
      const events = await EventApi.getUserEventsFiltered(eventsParams);

      // Fetch assignments for the date range
      const assignmentsParams = {
        userId: user.id,
        startDate: startIso,
        endDate: endIso
      };
      const assignments = await AssignmentApi.getUserAssignmentsFiltered(assignmentsParams);

      // Ingest into canonical state
      if (Array.isArray(events) && events.length > 0) {
        ingestNestedEntities(events);
      }
      if (Array.isArray(assignments) && assignments.length > 0) {
        ingestNestedEntities(assignments);
      }

      logInfo(`[refreshCalendarRange] Fetched ${events?.length || 0} events, ${assignments?.length || 0} assignments`);

      return {
        events: events || [],
        assignments: assignments || []
      };
    } catch (error) {
      console.error('[refreshCalendarRange] Error fetching calendar range:', error);
      return { events: [], assignments: [] };
    }
  }, [user, ingestNestedEntities]);

  const createEventType = useCallback(async (data) => {
    const response = await EventTypeApi.createEventType(data);
    if (response.ok) {
      clearCache('event-types');
      console.log('🗑️ Cleared event-types cache after create');
    }
    return response;
  }, [clearCache]);

  const updateEventType = useCallback(async (eventTypeId, data) => {
    const response = await EventTypeApi.updateEventType(eventTypeId, data);
    if (response.ok) {
      clearCache('event-types');
      clearCache(`event-type_${eventTypeId}`);
      console.log(`🗑️ Cleared event-type caches after update: ${eventTypeId}`);
    }
    return response;
  }, [clearCache]);

  const deleteEventType = useCallback(async (eventTypeId) => {
    const response = await EventTypeApi.deleteEventType(eventTypeId);
    if (response.ok) {
      clearCache('event-types');
      clearCache(`event-type_${eventTypeId}`);
      console.log(`🗑️ Cleared event-type caches after delete: ${eventTypeId}`);
    }
    return response;
  }, [clearCache]);

  /**
   * Optimistically update an assignment in all relevant cache entries without clearing.
   * This allows instant UI updates while preserving data integrity.
   * @param {number} assignmentId - The assignment ID to update
   * @param {object} updatedFields - Partial assignment data to merge
   */
  const updateCachedAssignment = useCallback((assignmentId, updatedFields) => {
    const keysToUpdate = [];
    
    // Find all cache keys that might contain this assignment
    const allKeys = Array.from(mirrorCacheRef.current.keys());
    
    allKeys.forEach(key => {
      if (
        key === `assignment_${assignmentId}` ||
        key.startsWith('assignments') ||
        key.startsWith('user-assignments') ||
        key.startsWith('endeavor-assignments_') ||
        key.startsWith('event-assignments_')
      ) {
        keysToUpdate.push(key);
      }
    });
    
    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (!cached) return;
      
      let updated = false;
      
      // Handle single assignment cache
      if (key === `assignment_${assignmentId}`) {
        const newData = { ...cached.data, ...updatedFields };
        setCachedData(key, newData);
        updated = true;
      }
      // Handle array of assignments
      else if (Array.isArray(cached.data)) {
        const newArray = cached.data.map(assignment => {
          const id = assignment?.assignmentId || assignment?.assignmentID || assignment?.assignment_id || assignment?.id;
          if (String(id) === String(assignmentId)) {
            return { ...assignment, ...updatedFields };
          }
          return assignment;
        });
        
        // Only update if we found and modified the assignment
        if (newArray.some((a, i) => {
          const id = a?.assignmentId || a?.assignmentID || a?.assignment_id || a?.id;
          return String(id) === String(assignmentId) && a !== cached.data[i];
        })) {
          setCachedData(key, newArray);
          updated = true;
        }
      }
      
      if (updated) {
        logDebug(`✏️ Optimistically updated assignment ${assignmentId} in cache: ${key}`);
      }
    });
    
    // Note: Trigger increment removed - calling functions handle this after optimistic update completes
  }, [getCachedData, setCachedData]);

  const createAssignment = useCallback(async (data) => {
    const response = await AssignmentApi.createAssignment(data);
    if (response.ok) {
      // Parse and normalize the created assignment
      const created = await response.json();
      const normalized = normalizeAssignment(created, data);
      
      // Upsert into canonical state dictionary (mirroring delete's state update)
      if (normalized && normalized.assignmentId) {
        setState(prev => ({
          ...prev,
          assignmentsById: {
            ...prev.assignmentsById,
            [String(normalized.assignmentId)]: normalized
          },
          dataVersion: prev.dataVersion + 1
        }));
        console.log(`✅ Upserted assignment ${normalized.assignmentId} into canonical state`);
      }
      
      // Update caches by adding new assignment instead of clearing
      const allAssignmentsKey = generateCacheKey('user-assignments', { page: 0, size: 10000 });
      const cached = getCachedData(allAssignmentsKey);
      if (cached && Array.isArray(cached.data)) {
        const updatedAssignments = [...cached.data, normalized];
        setCachedData(allAssignmentsKey, updatedAssignments);
        console.log(`✅ Added assignment ${normalized.assignmentId} to cache`);
      }
      
      // Return normalized object for modal's use
      return normalized;
    }
    return response;
  }, [generateCacheKey, getCachedData, setCachedData, setState]);

  const updateAssignment = useCallback(async (assignmentId, data) => {
    try {
      console.log('DataContext.updateAssignment: outbound', { assignmentId, payload: data });
    } catch (e) { /* ignore stringify errors */ }

    // Store original state for rollback on failure
    const originalState = new Map();
    const keysToUpdate = Array.from(mirrorCacheRef.current.keys()).filter(key => 
      key === `assignment_${assignmentId}` ||
      key.startsWith('assignments') ||
      key.startsWith('user-assignments') ||
      key.startsWith('endeavor-assignments_') ||
      key.startsWith('event-assignments_')
    );
    
    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (cached) {
        originalState.set(key, JSON.parse(JSON.stringify(cached.data)));
      }
    });

    // Optimistically update cache before API call for instant UI feedback
    updateCachedAssignment(assignmentId, data);
    
    // Trigger components to refetch with the optimistically updated data
    setDataRefreshTrigger(prev => prev + 1);

    try {
      // API now returns parsed object or null (for 204), or throws on errors
      const serverData = await AssignmentApi.updateAssignment(assignmentId, data);

      // If server returned updated entity, merge it into cache (authoritative writeback)
      if (serverData) {
        console.log('DataContext.updateAssignment: success with server data', serverData);
        updateCachedAssignment(assignmentId, serverData);
        setDataRefreshTrigger(prev => prev + 1);
        return { ok: true, data: serverData };
      } else {
        // 204 No Content - optimistic update remains
        console.log('DataContext.updateAssignment: success (204 No Content), using optimistic update');
        return { ok: true, data: { ...data, assignmentId, id: assignmentId } };
      }
    } catch (error) {
      // Rollback optimistic update on ANY failure (network, HTTP error, parse error)
      console.error(`⚠️ Assignment update failed, rolling back optimistic changes for ${assignmentId}:`, error);
      originalState.forEach((data, key) => {
        setCachedData(key, data);
      });
      setDataRefreshTrigger(prev => prev + 1);
      throw error; // Re-throw for parent handler
    }
  }, [getCachedData, setCachedData, updateCachedAssignment]);

  // Mark assignment completed (uses dedicated PATCH endpoint). Falls back to updateAssignment
  const markAssignmentCompleted = useCallback(async (assignmentId) => {
    if (assignmentId === undefined || assignmentId === null) throw new Error('assignmentId required');
    
    // Store original state for rollback on failure
    const originalState = new Map();
    const keysToUpdate = Array.from(mirrorCacheRef.current.keys()).filter(key => 
      key === `assignment_${assignmentId}` ||
      key.startsWith('assignments') ||
      key.startsWith('user-assignments') ||
      key.startsWith('endeavor-assignments_') ||
      key.startsWith('event-assignments_')
    );
    
    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (cached) {
        originalState.set(key, JSON.parse(JSON.stringify(cached.data)));
      }
    });

    // Optimistically mark as completed
    updateCachedAssignment(assignmentId, { 
      completed: true, 
      completedAt: new Date().toISOString(),
      status: 'completed'
    });
    
    // Trigger components to refetch with the optimistically updated data
    setDataRefreshTrigger(prev => prev + 1);

    const response = await AssignmentApi.markAssignmentCompleted(assignmentId);
    
    if (!response.ok) {
      // Rollback optimistic update on failure
      console.warn(`⚠️ Mark completed failed, rolling back optimistic changes for ${assignmentId}`);
      originalState.forEach((data, key) => {
        setCachedData(key, data);
      });
      setDataRefreshTrigger(prev => prev + 1);
    }
    
    return response;
  }, [getCachedData, setCachedData, updateCachedAssignment]);

  // Bulk-mark multiple assignments as completed in a single request
  const bulkMarkAssignmentsCompleted = useCallback(async (assignmentIds) => {
    if (!assignmentIds || assignmentIds.length === 0) return { ok: true, updated: 0 };

    // Store original state for rollback on failure
    const originalState = new Map();
    const keysToUpdate = Array.from(mirrorCacheRef.current.keys()).filter(key =>
      key.startsWith('assignments') ||
      key.startsWith('user-assignments') ||
      key.startsWith('endeavor-assignments_') ||
      key.startsWith('event-assignments_') ||
      assignmentIds.some(id => key === `assignment_${id}`)
    );

    keysToUpdate.forEach(key => {
      const cached = getCachedData(key);
      if (cached) {
        originalState.set(key, JSON.parse(JSON.stringify(cached.data)));
      }
    });

    // Optimistically mark all as completed
    assignmentIds.forEach(id => {
      updateCachedAssignment(id, {
        completed: true,
        completedAt: new Date().toISOString(),
        status: 'completed'
      });
    });

    setDataRefreshTrigger(prev => prev + 1);

    try {
      const response = await AssignmentApi.bulkMarkAssignmentsCompleted(assignmentIds);

      if (!response.ok) {
        // Rollback all optimistic updates on failure
        console.warn('⚠️ Bulk mark completed failed, rolling back');
        originalState.forEach((data, key) => {
          setCachedData(key, data);
        });
        setDataRefreshTrigger(prev => prev + 1);
      }

      return response;
    } catch (error) {
      // Rollback on network error
      console.warn('⚠️ Bulk mark completed error, rolling back', error);
      originalState.forEach((data, key) => {
        setCachedData(key, data);
      });
      setDataRefreshTrigger(prev => prev + 1);
      throw error;
    }
  }, [getCachedData, setCachedData, updateCachedAssignment]);

  // Shared‑course helpers (hoisted above toggleAssignmentCompleted which depends on them)
  const isSharedCourse = useCallback((endeavorId) => {
    const course = state.coursesById[endeavorId];
    return course?.isShared === true;
  }, [state.coursesById]);

  const updateAssignmentProgress = useCallback(async (assignmentId, updates) => {
    try {
      const resp = await ProgressApi.updateAssignmentProgress(assignmentId, updates);
      if (!resp.ok) { console.warn('[DataContext] updateAssignmentProgress failed', resp.status); return null; }
      const result = await resp.json();
      // Update local progress overlay
      const endeavorId = result.endeavorId || result.endeavor_id;
      if (endeavorId) {
        setProgressOverlays(prev => {
          const existing = prev[endeavorId] || { assignments: {}, events: {} };
          return {
            ...prev,
            [endeavorId]: {
              ...existing,
              assignments: { ...existing.assignments, [assignmentId]: result },
            },
          };
        });
      }
      return result;
    } catch (err) {
      console.warn('[DataContext] updateAssignmentProgress error', err);
      return null;
    }
  }, []);

  // Toggle assignment completion with centralized tracking
  const toggleAssignmentCompleted = useCallback(async (assignment, { source = 'unknown' } = {}) => {
    const id = assignment.assignmentId || assignment.id;
    if (!id) throw new Error('Assignment ID required');

    const currentlyDone = !!(assignment.completed ?? assignment.isDone);
    const endeavorId = assignment.endeavorId || assignment.endeavorID || assignment.endeavor_id;
    const eventId = assignment.eventId || assignment.eventID || assignment.event_id;
    const title = assignment.title || assignment.name;

    try {
      // For shared courses, use the progress overlay API instead of updating the assignment entity
      if (endeavorId && isSharedCourse(endeavorId)) {
        const newCompleted = !currentlyDone;
        await updateAssignmentProgress(id, { completed: newCompleted });

        try {
          UsageEventsTracker.recordUserAction(newCompleted ? 'ASSIGNMENT_COMPLETE' : 'ASSIGNMENT_UNCOMPLETE', {
            action: newCompleted ? 'complete' : 'uncomplete',
            category: 'assignments',
            entityType: 'assignment',
            entityId: id,
            label: title,
            metadata: { assignmentId: id, endeavorId, eventId, source, shared: true }
          });
        } catch (trackError) {
          console.warn('toggleAssignmentCompleted: Failed to track', trackError);
        }
        return;
      }

      if (!currentlyDone) {
        // Mark as complete
        await markAssignmentCompleted(id);
        
        // Track completion event
        try {
          UsageEventsTracker.recordUserAction('ASSIGNMENT_COMPLETE', {
            action: 'complete',
            category: 'assignments',
            entityType: 'assignment',
            entityId: id,
            label: title,
            metadata: {
              assignmentId: id,
              endeavorId,
              eventId,
              source
            }
          });
        } catch (trackError) {
          console.warn('toggleAssignmentCompleted: Failed to track completion', trackError);
        }
      } else {
        // Mark as incomplete - requires full payload
        const payload = buildAssignmentUpdatePayload(assignment, {
          completed: false,
          completedDatetime: null
        });
        
        await updateAssignment(id, payload);
        
        // Track uncompletion event
        try {
          UsageEventsTracker.recordUserAction('ASSIGNMENT_UNCOMPLETE', {
            action: 'uncomplete',
            category: 'assignments',
            entityType: 'assignment',
            entityId: id,
            label: title,
            metadata: {
              assignmentId: id,
              endeavorId,
              eventId,
              source
            }
          });
        } catch (trackError) {
          console.warn('toggleAssignmentCompleted: Failed to track uncompletion', trackError);
        }
      }
    } catch (error) {
      // Re-throw for component-level error handling
      throw error;
    }
  }, [markAssignmentCompleted, updateAssignment, isSharedCourse, updateAssignmentProgress]);

  const deleteAssignment = useCallback(async (assignmentId) => {
    const response = await AssignmentApi.deleteAssignment(assignmentId);
    if (response.ok) {
      // Update cache directly without triggering entity extraction
      const allAssignmentsKey = `user-assignments_page-0_size-10000`;
      const cached = mirrorCacheRef.current.get(allAssignmentsKey);
      if (cached && Array.isArray(cached.data)) {
        const updatedAssignments = cached.data.filter(a => {
          const id = a.assignmentId || a.id;
          return String(id) !== String(assignmentId);
        });
        mirrorCacheRef.current.set(allAssignmentsKey, { data: updatedAssignments, timestamp: Date.now() });
      }
      
      // Update state with dataVersion increment but WITHOUT entity extraction
      setState(prev => {
        const newAssignmentsById = { ...prev.assignmentsById };
        delete newAssignmentsById[String(assignmentId)];
        return {
          ...prev,
          assignmentsById: newAssignmentsById,
          dataVersion: prev.dataVersion + 1
        };
      });
      
      console.log(`🗑️ Deleted assignment: ${assignmentId}`);
    }
    return response;
  }, [setState]);

  // ✅ NEW: Document fetching methods with caching
  const getDocumentsByUser = useCallback(async (userId, params = {}) => {
    if (!userId) throw new Error('userId required for getDocumentsByUser');
    const key = generateCacheKey('documents_user', { userId, ...params });
    await awaitDashboardHydrationIfNeeded(key);
    return fetchWithCache(key, () => DocumentApi.getDocumentsByUser(userId, params));
  }, [fetchWithCache, generateCacheKey, awaitDashboardHydrationIfNeeded]);

  /**
   * SWR variant: returns cached documents instantly, refreshes in background.
   * @param {string}  userId
   * @param {Object}  params    - query params (page, size, sort, etc.)
   * @param {Function} onUpdate - called with fresh data after background revalidation
   */
  const getDocumentsByUserSWR = useCallback(async (userId, params = {}, onUpdate) => {
    if (!userId) throw new Error('userId required for getDocumentsByUserSWR');
    // Exclude _ts cache-buster from the cache key so repeated opens share cache
    const { _ts, ...cleanParams } = params;
    const key = generateCacheKey('documents_user', { userId, ...cleanParams });
    return fetchWithSWR(key, () => DocumentApi.getDocumentsByUser(userId, params), { onUpdate });
  }, [fetchWithSWR, generateCacheKey]);

  const getDocumentsByEndeavor = useCallback(async (endeavorId, params = {}) => {
    if (!endeavorId) throw new Error('endeavorId required for getDocumentsByEndeavor');
    const key = generateCacheKey('documents_endeavor', { endeavorId, ...params });
    await awaitDashboardHydrationIfNeeded(key);
    return fetchWithCache(key, () => DocumentApi.getDocumentsByEndeavor(endeavorId, params));
  }, [fetchWithCache, generateCacheKey, awaitDashboardHydrationIfNeeded]);

  const getDocumentById = useCallback(async (documentId) => {
    if (!documentId) throw new Error('documentId required for getDocumentById');
    const key = `document_${documentId}`;
    const data = await fetchWithCache(key, () => DocumentApi.getDocument(documentId));
    return data || null;
  }, [fetchWithCache]);

  // ===== AUDIO CONTENT METHODS =====
  
  /**
   * Get user's audio content (simple list)
   * @param {Object} options - Filter options
   * @param {string} options.format - Filter by format: "podcast" or "audiobook"
   * @param {number} options.endeavorId - Filter by endeavor ID
   */
  const getUserAudioContent = useCallback(async (options = {}) => {
    const key = generateCacheKey('audio-content_user', options);
    return fetchWithCache(key, () => AudioContentApi.getUserAudioContent(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * List and filter audio content with pagination
   * @param {Object} options - Query options (page, size, filters)
   */
  const listAudioContent = useCallback(async (options = {}) => {
    const key = generateCacheKey('audio-content_list', options);
    return fetchWithCache(key, () => AudioContentApi.listAudioContent(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * List audio content with stale-while-revalidate.
   * Returns cached items instantly and refreshes in the background.
   * @param {Object} options   - Query options (page, size, format, etc.)
   * @param {Function} onUpdate - Called with fresh data when background fetch completes
   */
  const listAudioContentSWR = useCallback(async (options = {}, onUpdate) => {
    const key = generateCacheKey('audio-content_list', options);
    return fetchWithSWR(key, () => AudioContentApi.listAudioContent(options), { onUpdate });
  }, [fetchWithSWR, generateCacheKey]);

  /**
   * Get single audio content by ID
   * @param {number} audioContentId - Audio content ID
   */
  const getAudioContent = useCallback(async (audioContentId) => {
    const key = `audio-content_${audioContentId}`;
    return fetchWithCache(key, () => AudioContentApi.getAudioContent(audioContentId));
  }, [fetchWithCache]);

  /**
   * Get audio content by endeavor
   * @param {number} endeavorId - Endeavor ID
   * @param {Object} options - Filter options
   */
  const getEndeavorAudioContent = useCallback(async (endeavorId, options = {}) => {
    const key = generateCacheKey('audio-content_endeavor', { endeavorId, ...options });
    return fetchWithCache(key, () => AudioContentApi.getEndeavorAudioContent(endeavorId, options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * Get audio content statistics
   */
  const getAudioContentStats = useCallback(async () => {
    const key = 'audio-content_stats';
    return fetchWithCache(key, () => AudioContentApi.getAudioContentStats());
  }, [fetchWithCache]);

  /**
   * Create audio content (cache invalidation)
   * @param {Object} data - Audio content data
   */
  const createAudioContent = useCallback(async (data) => {
    const response = await AudioContentApi.createAudioContent(data);
    if (response.ok) {
      clearCache('audio-content');
      if (data.endeavorId) {
        clearCache(`audio-content_endeavor_${data.endeavorId}`);
      }
      console.log('🗑️ Cleared audio content caches after create');
    }
    return response;
  }, [clearCache]);

  /**
   * Delete audio content (cache invalidation)
   * @param {number} audioContentId - Audio content ID
   */
  const deleteAudioContent = useCallback(async (audioContentId) => {
    const response = await AudioContentApi.deleteAudioContent(audioContentId);
    if (response.ok) {
      clearCache('audio-content');
      clearCache(`audio-content_${audioContentId}`);
      console.log(`🗑️ Cleared audio content caches after delete: ${audioContentId}`);
    }
    return response;
  }, [clearCache]);

  /**
   * Update audio content metadata (cache invalidation)
   * @param {number} audioContentId - Audio content ID
   * @param {Object} data - Updated data (e.g., { title, description })
   */
  const updateAudioContent = useCallback(async (audioContentId, data) => {
    const response = await AudioContentApi.updateAudioContent(audioContentId, data);
    if (response.ok) {
      clearCache('audio-content');
      clearCache(`audio-content_${audioContentId}`);
      console.log(`🗑️ Cleared audio content caches after update: ${audioContentId}`);
    }
    return response;
  }, [clearCache]);

  // ===== FLASHCARD METHODS =====
  
  /**
   * Get flashcard decks (simple list)
   * @param {Object} options - Filter options
   * @param {number} options.endeavorId - Filter by endeavor ID
   */
  const getFlashcardDecks = useCallback(async (options = {}) => {
    const key = generateCacheKey('flashcard-decks', options);
    return fetchWithCache(key, () => FlashcardApi.getFlashcardDecks(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * List and filter flashcard decks with pagination
   * @param {Object} options - Query options (page, size, filters)
   */
  const listFlashcardDecks = useCallback(async (options = {}) => {
    const key = generateCacheKey('flashcard-decks_list', options);
    return fetchWithCache(key, () => FlashcardApi.listFlashcardDecks(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * Get single flashcard deck with cards
   * @param {number} deckId - Flashcard deck ID
   */
  const getFlashcardDeck = useCallback(async (deckId) => {
    const key = `flashcard-deck_${deckId}`;
    return fetchWithCache(key, () => FlashcardApi.getFlashcardDeck(deckId));
  }, [fetchWithCache]);

  /**
   * Get due flashcards for review
   * @param {number} deckId - Flashcard deck ID
   */
  const getDueCards = useCallback(async (deckId) => {
    const key = `flashcard-deck_${deckId}_due`;
    // Short cache time for due cards (1 minute) as this changes frequently
    return fetchWithCache(key, () => FlashcardApi.getDueCards(deckId), 60 * 1000);
  }, [fetchWithCache]);

  /**
   * Get deck statistics
   * @param {number} deckId - Flashcard deck ID
   */
  const getDeckStats = useCallback(async (deckId) => {
    const key = `flashcard-deck_${deckId}_stats`;
    // Short cache time for stats (1 minute) as this changes frequently
    return fetchWithCache(key, () => FlashcardApi.getDeckStats(deckId), 60 * 1000);
  }, [fetchWithCache]);

  /**
   * Create flashcard deck (cache invalidation)
   * @param {Object} data - Flashcard deck data
   */
  const createFlashcardDeck = useCallback(async (data) => {
    const response = await FlashcardApi.createFlashcardDeck(data);
    if (response.ok) {
      clearCache('flashcard-decks');
      console.log('🗑️ Cleared flashcard deck caches after create');
    }
    return response;
  }, [clearCache]);

  /**
   * Update flashcard deck (cache invalidation)
   * @param {number} deckId - Flashcard deck ID
   * @param {Object} data - Updated deck data
   */
  const updateFlashcardDeck = useCallback(async (deckId, data) => {
    const response = await FlashcardApi.updateFlashcardDeck(deckId, data);
    if (response.ok) {
      clearCache('flashcard-decks');
      clearCache(`flashcard-deck_${deckId}`);
      console.log(`🗑️ Cleared flashcard deck caches after update: ${deckId}`);
    }
    return response;
  }, [clearCache]);

  /**
   * Update card review (cache invalidation)
   * @param {number} cardId - Flashcard card ID
   * @param {Object} reviewData - Review data
   */
  const updateCardReview = useCallback(async (cardId, reviewData) => {
    const response = await FlashcardApi.updateCardReview(cardId, reviewData);
    if (response.ok) {
      // Invalidate due cards and stats caches
      clearCache('flashcard-deck_');
      console.log('🗑️ Cleared flashcard review caches after update');
    }
    return response;
  }, [clearCache]);

  /**
   * Delete flashcard deck (cache invalidation)
   * @param {number} deckId - Flashcard deck ID
   */
  const deleteFlashcardDeck = useCallback(async (deckId) => {
    const response = await FlashcardApi.deleteFlashcardDeck(deckId);
    if (response.ok) {
      clearCache('flashcard-decks');
      clearCache(`flashcard-deck_${deckId}`);
      console.log(`🗑️ Cleared flashcard deck caches after delete: ${deckId}`);
    }
    return response;
  }, [clearCache]);

  // ===== QUIZ METHODS =====
  
  /**
   * Get quiz sets (simple list)
   * @param {Object} options - Filter options
   * @param {number} options.endeavorId - Filter by endeavor ID
   */
  const getQuizSets = useCallback(async (options = {}) => {
    const key = generateCacheKey('quiz-sets', options);
    return fetchWithCache(key, () => QuizApi.getQuizSets(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * List and filter quiz sets with pagination
   * @param {Object} options - Query options (page, size, filters)
   */
  const listQuizSets = useCallback(async (options = {}) => {
    const key = generateCacheKey('quiz-sets_list', options);
    return fetchWithCache(key, () => QuizApi.listQuizSets(options));
  }, [fetchWithCache, generateCacheKey]);

  /**
   * Get single quiz set with questions
   * @param {number} quizSetId - Quiz set ID
   */
  const getQuizSet = useCallback(async (quizSetId) => {
    const key = `quiz-set_${quizSetId}`;
    return fetchWithCache(key, () => QuizApi.getQuizSet(quizSetId));
  }, [fetchWithCache]);

  /**
   * Get attempt results
   * @param {number} attemptId - Quiz attempt ID
   */
  const getAttemptResults = useCallback(async (attemptId) => {
    const key = `quiz-attempt_${attemptId}`;
    return fetchWithCache(key, () => QuizApi.getAttemptResults(attemptId));
  }, [fetchWithCache]);

  /**
   * Get latest attempt for a quiz set
   * @param {number} quizSetId - Quiz set ID
   */
  const getLatestAttempt = useCallback(async (quizSetId) => {
    const key = `quiz-attempt-latest_${quizSetId}`;
    return fetchWithCache(key, () => QuizApi.getLatestAttempt(quizSetId));
  }, [fetchWithCache]);

  /**
   * Get all attempts for a quiz set
   * @param {number} quizSetId - Quiz set ID
   */
  const getQuizAttempts = useCallback(async (quizSetId) => {
    const key = `quiz-attempts_${quizSetId}`;
    return fetchWithCache(key, () => QuizApi.getQuizAttempts(quizSetId));
  }, [fetchWithCache]);

  /**
   * Invalidate quiz attempt caches for a quiz set
   * @param {number} quizSetId - Quiz set ID
   */
  const invalidateQuizAttemptCaches = useCallback((quizSetId) => {
    clearCache(`quiz-attempt-latest_${quizSetId}`);
    clearCache(`quiz-attempts_${quizSetId}`);
    console.log(`🗑️ Cleared quiz attempt caches for quiz set: ${quizSetId}`);
  }, [clearCache]);

  /**
   * Create quiz set (cache invalidation)
   * @param {Object} data - Quiz set data
   */
  const createQuizSet = useCallback(async (data) => {
    const response = await QuizApi.createQuizSet(data);
    if (response.ok) {
      clearCache('quiz-sets');
      console.log('🗑️ Cleared quiz set caches after create');
    }
    return response;
  }, [clearCache]);

  /**
   * Start quiz attempt (no cache, returns fresh attempt)
   * @param {number} quizSetId - Quiz set ID
   */
  const startQuizAttempt = useCallback(async (quizSetId) => {
    return QuizApi.startQuizAttempt(quizSetId);
  }, []);

  /**
   * Submit quiz answers (cache invalidation)
   * @param {number} attemptId - Quiz attempt ID
   * @param {Object} data - Submission data
   */
  const submitQuizAnswers = useCallback(async (attemptId, data) => {
    const response = await QuizApi.submitQuizAnswers(attemptId, data);
    if (response.ok) {
      clearCache(`quiz-attempt_${attemptId}`);
      console.log(`🗑️ Cleared quiz attempt cache after submission: ${attemptId}`);
    }
    return response;
  }, [clearCache]);

  /**
   * Delete quiz set (cache invalidation)
   * @param {number} quizSetId - Quiz set ID
   */
  const deleteQuizSet = useCallback(async (quizSetId) => {
    const response = await QuizApi.deleteQuizSet(quizSetId);
    if (response.ok) {
      clearCache('quiz-sets');
      clearCache(`quiz-set_${quizSetId}`);
      invalidateQuizAttemptCaches(quizSetId);
      console.log(`🗑️ Cleared quiz set caches after delete: ${quizSetId}`);
    }
    return response;
  }, [clearCache, invalidateQuizAttemptCaches]);

  /**
   * Update quiz set (cache invalidation)
   * @param {number} quizSetId - Quiz set ID
   * @param {Object} data - Updated quiz set data (e.g., { title, description })
   */
  const updateQuizSet = useCallback(async (quizSetId, data) => {
    const response = await QuizApi.updateQuizSet(quizSetId, data);
    if (response.ok) {
      clearCache('quiz-sets');
      clearCache(`quiz-set_${quizSetId}`);
      console.log(`🗑️ Cleared quiz set caches after update: ${quizSetId}`);
    }
    return response;
  }, [clearCache]);

  // ============ Document Status Methods ============
  const fetchDocumentStatuses = useCallback(async () => {
    const cacheKey = 'document-statuses-v1';
    const cached = getCachedData(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < 24 * 60 * 60 * 1000) {
      setState(prev => ({ ...prev, documentStatuses: cached.data }));
      return cached.data;
    }

    try {
      const response = await DocumentApi.getDocumentStatuses();
      if (!response.ok) throw new Error('Failed to fetch document statuses');
      
      const data = await response.json();
      setCachedData(cacheKey, data);
      setState(prev => ({ ...prev, documentStatuses: data }));
      return data;
    } catch (error) {
      console.error('[DataContext] Error fetching document statuses:', error);
      return [];
    }
  }, [getCachedData, setCachedData]);

  const getDocumentStatusIdByName = useCallback((name) => {
    if (!name || !state.documentStatuses || !state.documentStatuses.length) return null;

    const nameLower = name.toLowerCase();
    
    // Direct match
    const directMatch = state.documentStatuses.find(s => s.name.toLowerCase() === nameLower);
    if (directMatch) return directMatch.id;

    // Fuzzy match for RAG Chunk variants (handles typo)
    if (nameLower.includes('rag') && (nameLower.includes('chunk') || nameLower.includes('chuck'))) {
      const ragProcessed = state.documentStatuses.find(s => 
        s.name.toLowerCase().includes('rag') && 
        (s.name.toLowerCase().includes('chunks processed') || s.name.toLowerCase().includes('chunks processed'))
      );
      if (ragProcessed) return ragProcessed.id;
    }

    // Partial match fallback
    const partialMatch = state.documentStatuses.find(s => 
      s.name.toLowerCase().includes(nameLower) || nameLower.includes(s.name.toLowerCase())
    );
    return partialMatch ? partialMatch.id : null;
  }, [state.documentStatuses]);

  // ============ Document Type Methods ============
  const fetchDocumentTypes = useCallback(async () => {
    const cacheKey = 'document-types-v1';
    const cached = getCachedData(cacheKey);
    
    console.log('[DataContext] fetchDocumentTypes called, checking cache...');
    
    // Cache for 12 hours (same as dashboard data)
    if (cached && (Date.now() - cached.timestamp) < 12 * 60 * 60 * 1000) {
      console.log('[DataContext] Using cached document types:', cached.data.length, 'types');
      setState(prev => ({ ...prev, documentTypes: cached.data }));
      return cached.data;
    }

    try {
      console.log('[DataContext] Fetching document types from API...');
      const response = await DocumentTypeApi.getDocumentTypes();
      if (!response.ok) throw new Error('Failed to fetch document types');
      
      const data = await response.json();
      const types = Array.isArray(data) ? data : [];
      console.log('[DataContext] Fetched and cached', types.length, 'document types');
      setCachedData(cacheKey, types);
      setState(prev => ({ ...prev, documentTypes: types }));
      return types;
    } catch (error) {
      console.error('[DataContext] Error fetching document types:', error);
      // Return cached data even if stale, or empty array
      return cached?.data || [];
    }
  }, [getCachedData, setCachedData]);

  const getDocumentTypeIdByName = useCallback((name) => {
    if (!name || !state.documentTypes || !state.documentTypes.length) return null;

    const nameLower = name.toLowerCase();
    
    // Direct match
    const directMatch = state.documentTypes.find(t => t.name.toLowerCase() === nameLower);
    if (directMatch) return directMatch.id;

    // Partial match fallback
    const partialMatch = state.documentTypes.find(t => 
      t.name.toLowerCase().includes(nameLower) || nameLower.includes(t.name.toLowerCase())
    );
    return partialMatch ? partialMatch.id : null;
  }, [state.documentTypes]);

  // Auto-fetch document types on mount
  useEffect(() => {
    if (user) {
      fetchDocumentTypes();
    }
  }, [user, fetchDocumentTypes]);

  // Auto-fetch document statuses on mount
  useEffect(() => {
    if (user) {
      fetchDocumentStatuses();
    }
  }, [user, fetchDocumentStatuses]);

  // ============ Course Notes Methods ============
  /**
   * Fetch notes for a specific course/endeavor with content batched
   * Caches the result to avoid repeated API calls
   * @param {number} endeavorId - Course endeavor ID
   * @param {boolean} forceRefresh - Force bypass cache
   * @returns {Promise<Array>} - Array of notes with content
   */
  const fetchNotesByEndeavor = useCallback(async (endeavorId, forceRefresh = false) => {
    const userId = user?.id || user?.userId;
    if (!userId || !endeavorId) {
      console.warn('[DataContext] fetchNotesByEndeavor: Missing userId or endeavorId');
      return [];
    }

    const cacheKey = `notes-endeavor-${endeavorId}-${userId}`;
    const cached = getCachedData(cacheKey);

    console.log('[DataContext] fetchNotesByEndeavor called for endeavorId:', endeavorId);

    // Cache for 5 minutes (notes change more frequently than document types)
    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < 5 * 60 * 1000) {
      console.log('[DataContext] Using cached notes for endeavor:', endeavorId, '(', cached.data.length, 'notes)');
      return cached.data;
    }

    try {
      console.log('[DataContext] Fetching notes list from API for endeavor:', endeavorId);
      
      // Fetch list of notes (metadata only - no content)
      const response = await DocumentNotesApi.getNotesByEndeavor(endeavorId, userId);
      const notesList = response.content || [];
      
      if (notesList.length === 0) {
        console.log('[DataContext] No notes found for endeavor:', endeavorId);
        setCachedData(cacheKey, []);
        return [];
      }

      console.log('[DataContext] Successfully cached', notesList.length, 'notes (metadata only)');
      setCachedData(cacheKey, notesList);
      return notesList;
      
    } catch (error) {
      console.error('[DataContext] Error fetching notes for endeavor:', endeavorId, error);
      // Return cached data even if stale, or empty array
      return cached?.data || [];
    }
  }, [user, getCachedData, setCachedData]);

  /**
   * Fetch content for a specific note (lazy-loaded, with caching)
   * @param {number} noteId - Note document ID
   * @param {boolean} forceRefresh - Force bypass cache
   * @returns {Promise<string>} - Note content (markdown)
   */
  const fetchNoteContent = useCallback(async (noteId, forceRefresh = false) => {
    if (!noteId) {
      console.warn('[DataContext] fetchNoteContent: Missing noteId');
      return '';
    }

    const cacheKey = `note-content-${noteId}`;
    const cached = getCachedData(cacheKey);

    // Cache for 5 minutes
    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < 5 * 60 * 1000) {
      console.log('[DataContext] Using cached content for note:', noteId);
      return cached.data;
    }

    try {
      console.log('[DataContext] Fetching content for note:', noteId);
      const contentResponse = await DocumentNotesApi.getNoteContent(noteId);
      const content = contentResponse?.content || '';
      
      setCachedData(cacheKey, content);
      return content;
    } catch (error) {
      console.error('[DataContext] Error fetching note content:', noteId, error);
      // Return cached data even if stale, or empty string
      return cached?.data || '';
    }
  }, [getCachedData, setCachedData]);

  /**
   * Invalidate notes cache for a specific endeavor (call after creating/updating/deleting a note)
   * @param {number} endeavorId - Course endeavor ID
   */
  const invalidateNotesCache = useCallback((endeavorId, noteId = null) => {
    const userId = user?.id || user?.userId;
    if (!userId || !endeavorId) return;
    
    console.log('[DataContext] Invalidating notes cache for endeavor:', endeavorId, noteId ? `and note: ${noteId}` : '');
    
    // Invalidate endeavor notes list from in-memory cache
    const listCacheKey = `notes-endeavor-${endeavorId}-${userId}`;
    mirrorCacheRef.current.delete(listCacheKey);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(listCacheKey);
      return newCache;
    });
    
    // If specific note ID provided, also invalidate its content cache
    if (noteId) {
      const contentCacheKey = `note-content-${noteId}`;
      mirrorCacheRef.current.delete(contentCacheKey);
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(contentCacheKey);
        return newCache;
      });
    } else {
      // If no specific note, clear all note content caches (could be any note in this endeavor)
      setCache(prev => {
        const newCache = new Map(prev);
        Array.from(newCache.keys()).forEach(key => {
          if (key.startsWith('note-content-')) {
            newCache.delete(key);
            mirrorCacheRef.current.delete(key);
          }
        });
        return newCache;
      });
    }
    
    console.log('[DataContext] Notes cache invalidated in memory for endeavor:', endeavorId);
  }, [user]);

  // === Enrollment & Progress helpers ===

  const loadEnrollments = useCallback(async () => {
    try {
      const resp = await EnrollmentApi.getMyEnrollments();
      if (!resp.ok) { console.warn('[DataContext] loadEnrollments failed', resp.status); return; }
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data?.content || []);
      const map = {};
      list.forEach(e => {
        const eid = e.endeavorId || e.endeavor_id;
        if (eid) {
          map[eid] = {
            enrollmentId: e.enrollmentId || e.id,
            role: e.role,
            status: e.status,
            enrolledAt: e.enrolledAt || e.createdAt,
          };
        }
      });
      setEnrollmentsById(map);
    } catch (err) {
      console.warn('[DataContext] loadEnrollments error', err);
    }
  }, []);

  const loadProgressForEndeavor = useCallback(async (endeavorId) => {
    try {
      const resp = await ProgressApi.getEndeavorProgress(endeavorId);
      if (!resp.ok) { console.warn('[DataContext] loadProgressForEndeavor failed', resp.status); return; }
      const data = await resp.json();
      const assignmentProgress = {};
      const eventProgress = {};
      if (Array.isArray(data.assignments)) {
        data.assignments.forEach(p => {
          const aid = p.assignmentId || p.assignment_id;
          if (aid) assignmentProgress[aid] = p;
        });
      }
      if (Array.isArray(data.events)) {
        data.events.forEach(p => {
          const eid = p.eventId || p.event_id;
          if (eid) eventProgress[eid] = p;
        });
      }
      setProgressOverlays(prev => ({
        ...prev,
        [endeavorId]: { assignments: assignmentProgress, events: eventProgress },
      }));
    } catch (err) {
      console.warn('[DataContext] loadProgressForEndeavor error', err);
    }
  }, []);

  const updateEventProgress = useCallback(async (eventId, updates) => {
    try {
      const resp = await ProgressApi.updateEventProgress(eventId, updates);
      if (!resp.ok) { console.warn('[DataContext] updateEventProgress failed', resp.status); return null; }
      const result = await resp.json();
      const endeavorId = result.endeavorId || result.endeavor_id;
      if (endeavorId) {
        setProgressOverlays(prev => {
          const existing = prev[endeavorId] || { assignments: {}, events: {} };
          return {
            ...prev,
            [endeavorId]: {
              ...existing,
              events: { ...existing.events, [eventId]: result },
            },
          };
        });
      }
      return result;
    } catch (err) {
      console.warn('[DataContext] updateEventProgress error', err);
      return null;
    }
  }, []);

  // Enrollment-aware helper functions
  const getEnrollmentRole = useCallback((endeavorId) => {
    return enrollmentsById[endeavorId]?.role || null;
  }, [enrollmentsById]);

  const isCourseOwnerOrProfessor = useCallback((endeavorId) => {
    const role = enrollmentsById[endeavorId]?.role;
    return role === 'owner' || role === 'professor';
  }, [enrollmentsById]);

  const getEffectiveCompleted = useCallback((assignment, endeavorId) => {
    if (!isSharedCourse(endeavorId)) return assignment?.completed || false;
    const overlay = progressOverlays[endeavorId];
    const aid = assignment?.assignmentId || assignment?.id;
    if (overlay?.assignments?.[aid]) return overlay.assignments[aid].completed || false;
    return false;
  }, [isSharedCourse, progressOverlays]);

  const getEffectiveDueDate = useCallback((assignment, endeavorId) => {
    const baseDue = assignment?.dueDatetime || assignment?.dueDate;
    if (!isSharedCourse(endeavorId)) return baseDue;
    const overlay = progressOverlays[endeavorId];
    const aid = assignment?.assignmentId || assignment?.id;
    return overlay?.assignments?.[aid]?.personalDueDatetime || baseDue;
  }, [isSharedCourse, progressOverlays]);

  // ✅ NEW: Derived courses array from canonical coursesById state
  // This ensures courses persist across component remounts (navigation)
  const courses = useMemo(() => {
    const userId = user?.id || user?.userId;
    if (!userId) return [];

    const coursesList = Object.values(state.coursesById || {});
    console.log('[DataContext] Deriving courses array:', coursesList.length, 'courses');

    // Enrich each course with syllabus information from cache
    return coursesList.map(course => {
      const endeavorId = course.endeavorId || course.id;

      // Check if syllabusDocument is already embedded
      if (course.syllabusDocument) {
        const syllabusDoc = course.syllabusDocument;
        const syllabusDocId = syllabusDoc.id || syllabusDoc.documentId;
        const syllabusDocName = syllabusDoc.name || syllabusDoc.title || syllabusDoc.fileName || 'Syllabus';
        
        return {
          ...course,
          syllabusDocId,
          syllabusDocName
        };
      }

      // Fallback: check cached syllabus documents
      try {
        const cacheKey = generateCacheKey('documents_user', {
          userId,
          endeavorId,
          name: 'syllabus',
          page: 0,
          size: 1,
          sort: 'createdAt,desc'
        });

        const cached = getCachedData(cacheKey);
        if (cached && cached.data) {
          const docsList = Array.isArray(cached.data) ? cached.data : [];
          const syllabusDoc = docsList[0];
          
          if (syllabusDoc) {
            const syllabusDocId = syllabusDoc.id || syllabusDoc.documentId;
            const syllabusDocName = syllabusDoc.name || syllabusDoc.title || syllabusDoc.fileName || 'Syllabus';
            
            return {
              ...course,
              syllabusDocId,
              syllabusDocName
            };
          }
        }
      } catch (err) {
        console.warn(`[DataContext] Failed to enrich course ${endeavorId} with syllabus:`, err);
      }

      return course;
    });
  }, [state.coursesById, user, generateCacheKey, getCachedData]);

  const value = ({
    // ✅ NEW: Canonical state and metadata
    state,
    status,
    selectors,
    courses, // ✅ NEW: Derived courses array (persists across navigation)
    // Wizard draft (single source of truth for CreateCourse flow)
    createCourseDraft,
    setCreateCourseDraft,
    addDraftEvent,
    addDraftAssignment,
    updateDraftEvent,
    deleteDraftEvent,
    updateDraftAssignment,
    deleteDraftAssignment,
    
    // Cache management
    generateCacheKey,
    isLoading,
    clearCache,
    getCachedData,
  ingestNestedEntities,
    invalidateUserAssignmentsCache,
  // Dashboard hydration
  hydrateDashboard,
  awaitDashboardHydrationIfNeeded,
  isDashboardHydrated: () => dashboardHydratedRef.current,
    dataRefreshTrigger, // ✅ NEW: Trigger for automatic trophy count refreshes
    
    // ✅ NEW: Authentication methods (cached)
    validateToken,
    isUserValid,
    getCurrentUser,
    
    // ✅ NEW: Health check methods (cached)
    checkHealth,
    isAiServiceAvailable,
    getEcosystemHealth,
    
    // Enrollment & Progress
    enrollmentsById,
    progressOverlays,
    loadEnrollments,
    loadProgressForEndeavor,
    updateAssignmentProgress,
    updateEventProgress,
    getEnrollmentRole,
    isSharedCourse,
    isCourseOwnerOrProfessor,
    getEffectiveCompleted,
    getEffectiveDueDate,

    // Endeavors
    getEndeavors,
    getEndeavorsByUser,
    getEndeavor,
    createEndeavor,
    updateEndeavor,
    patchEndeavor,
    deleteEndeavor,
    removeEndeavor,
    getEndeavorEvents,
    getEndeavorAssignments,
  // Aggregated (user scoped)
  getUserEvents,
  getUserAssignments,
    
    // ✅ NEW: Client-side filtering methods
    getFilteredEvents,
    getFilteredAssignments,
    getFilteredEndeavors,
    
    // Events
    getEvents,
    getEvent,
  getAssignmentsByEvent,
    createEvent,
    updateEvent,
    updateCachedEvent,
    deleteEvent,
    deleteEventSeries,
    refreshCalendarRange, // ✅ NEW: Targeted calendar range refetch
    
    // Event Types
    getEventTypes,
    getEventType,
    createEventType,
    updateEventType,
    deleteEventType,
    
    // Assignments
    getAssignments,
    getAssignment,
    createAssignment,
    updateAssignment,
    updateCachedAssignment,
    deleteAssignment,
    markAssignmentCompleted,
    bulkMarkAssignmentsCompleted,
    toggleAssignmentCompleted,
    
    // ✅ NEW: Documents
    getDocumentsByUser,
    getDocumentsByUserSWR,
    getDocumentsByEndeavor,
    getDocumentById,
    
    // Document Statuses
    documentStatuses: state.documentStatuses,
    fetchDocumentStatuses,
    getDocumentStatusIdByName,
    
    // ✅ NEW: Document Types
    documentTypes: state.documentTypes,
    fetchDocumentTypes,
    getDocumentTypeIdByName,
    
    // ✅ NEW: Course Notes
    fetchNotesByEndeavor,
    fetchNoteContent,
    invalidateNotesCache,
    
    // Audio Content
    getUserAudioContent,
    listAudioContent,
    listAudioContentSWR,
    getAudioContent,
    getEndeavorAudioContent,
    getAudioContentStats,
    createAudioContent,
    deleteAudioContent,
    updateAudioContent,
    
    // Flashcard Decks
    getFlashcardDecks,
    listFlashcardDecks,
    getFlashcardDeck,
    getDueCards,
    getDeckStats,
    createFlashcardDeck,
    updateFlashcardDeck,
    updateCardReview,
    deleteFlashcardDeck,
    
    // Quiz Sets
    getQuizSets,
    listQuizSets,
    getQuizSet,
    getAttemptResults,
    getLatestAttempt,
    getQuizAttempts,
    invalidateQuizAttemptCaches,
    createQuizSet,
    updateQuizSet,
    startQuizAttempt,
    submitQuizAnswers,
    deleteQuizSet,
  }); // Re-render consumers when state or status changes

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
